import { Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core'
import { environment } from '../../../../../environments/environment'
import { ActivatedRoute, RouterModule } from '@angular/router'
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms'
import { NgbModal, NgbModalRef, NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap'
import { AuthService } from '../../../../services/auth.service'
import {
  combineLatest,
  filter,
  map,
  Observable,
  of,
  Subject,
  take,
  takeUntil,
  tap,
  distinctUntilChanged
} from 'rxjs'
import { AuthState, User, UserRole } from '../../../../store/auth/auth.state'
import { Store, select } from '@ngrx/store'
import {
  blockEmployee,
  blockEmployeeSuccess,
  createEmployeeSuccess,
  deleteEmployee,
  loadEmployeeById,
  loadEmployees,
  loadUserById,
  portalAccessEmployee,
  portalAccessEmployeeSuccess,
  updateEmployee,
  updateEmployeeSuccess
} from '../../../../store/user/user.actions'
import { selectAllUsers, selectEmployeeDetail } from '../../../../store/user/user.selectors'
import { selectLoading } from '../../../../store/user/user.reducer'

import { PaginationComponent } from '../../template/pagination/pagination.component'
import { HasPermissionDirective } from '../../../../directives/has-permission.directive'
import { DeleteComponent } from '../../template/delete/delete.component'
import { BlockUnblockComponent } from '../../template/block-unblock/block-unblock.component'
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe'
import { CommonModule, NgIf } from '@angular/common'
import { ToastrService } from 'ngx-toastr'
import {
  createFamily,
  createFamilySuccess,
  deleteFamily,
  loadFamilies,
  updateFamily,
  updateFamilySuccess
} from '../../../../store/family/family.actions'
import { Family } from '../../../../store/family/family.state'
import { selectFamilies } from '../../../../store/family/family.reducer'
import {
  createBank,
  createBankSuccess,
  deleteBank,
  loadBanks,
  updateBank,
  updateBankSuccess
} from '../../../../store/bank/bank.actions'
import { selectBanks } from '../../../../store/bank/bank.reducer'
import {
  selectExperiences,
  selectTotalExperienceYears
} from '../../../../store/experience/experience.reducer'
import {
  createExperience,
  createExperienceSuccess,
  deleteExperience,
  deleteExperienceFailure,
  deleteExperienceSuccess,
  loadExperiences,
  updateExperience,
  updateExperienceFailure,
  updateExperienceSuccess
} from '../../../../store/experience/experience.actions'
import {
  createEducation,
  createEducationSuccess,
  deleteEducation,
  deleteEducationFailure,
  deleteEducationSuccess,
  loadEducations,
  updateEducation,
  updateEducationFailure,
  updateEducationSuccess
} from '../../../../store/education/education.actions'
import { selectEducations } from '../../../../store/education/education.reducer'
import { validateImage } from '../../../../utils/imageValidator'
import { Country } from '../../../../store/country/country.state'
import { City } from '../../../../store/city/city.state'
import { CityCountryState } from '../../../../store/state/state.state'
import { clearStates, loadStates } from '../../../../store/state/state.actions'
import { selectAllCountries } from '../../../../store/country/country.selectors'
import { loadCountries } from '../../../../store/country/country.actions'
import { selectAllCities } from '../../../../store/city/city.selectors'
import { clearCities, loadCities } from '../../../../store/city/city.actions'
import { selectAllStates } from '../../../../store/state/state.selectors'

import { selectAllBusinesses } from '../../../../store/business/business.selectors'
import { selectOffices } from '../../../../store/office/office.reducer'
import { Business } from '../../../../store/business/business.state'
import { Office } from '../../../../store/office/office.state'
import { Designation } from '../../../../store/designation/designation.state'
import { Department } from '../../../../store/department/department.state'
import { selectDesignations } from '../../../../store/designation/designation.reducer'
import { selectDepartments } from '../../../../store/department/department.reducer'
import {
  loadDesignations,
  resetDesignationState
} from '../../../../store/designation/designation.actions'
import { loadOffices } from '../../../../store/office/office.actions'
import { loadBusinesses } from '../../../../store/business/business.actions'
import {
  loadDepartments,
  resetDepartmentState
} from '../../../../store/department/department.actions'
import {
  createAssignedTo,
  createAssignedToSuccess,
  loadRoles
} from '../../../../store/role-permission/role-permission.actions'
import { Role } from '../../../../store/role-permission/role-permission.state'
import { selectRoles } from '../../../../store/role-permission/role-permission.reducer'
import {
  createAssignedPermissionSets,
  createAssignedPermissionSetsSuccess,
  loadAssignedPermissionSets,
  updateAssignedPermissionSetsSuccess
} from '../../../../store/assigned-permissionsets/assigned-permissionsets.actions'
import { selectAssignedPermissionSets } from '../../../../store/assigned-permissionsets/assigned-permissionsets.reducer'
import { Actions, ofType } from '@ngrx/effects'
import { selectUser } from '../../../../store/auth/auth.selectors'
import { SharedModule } from '../../../../shared/shared.module'
import { AttendancePolicyService } from '../../../../services/attendance-policy.service'
import { LeavePolicyService } from '../../../../services/leave-policy.service'
import {
  selectAllSalaries,
  selectEmployeeSalary
} from '../../../../store/employee-salary/employee-salary.reducers'
import {
  EmployeeSalary,
  StatutoryCompliances
} from '../../../../store/employee-salary/employee-salary.state'
import { eps } from '../../../../services/url.service'
import {
  loadAllSalaries,
  loadSalary
} from '../../../../store/employee-salary/employee-salary.actions'
import dayjs from 'dayjs'
import { Location } from '@angular/common'
import { selectTasklogs, selectTotalCount } from '../../../../store/task-log/task-log.reducer'
import { forgotPassword } from '../../../../store/auth/auth.actions'
import { NgSelectModule } from '@ng-select/ng-select'
import { UserService } from '../../../../services/user.service'
import { BankService } from '../../../../services/bank.service'
import { activityLogActions } from '../../../../store/task-log/task-log.actions'
import { GenerateLogMessagePipe } from '../../../../pipes/generate-log-message.pipe'
import { LoaderComponent } from '../../../../shared/components/loader/loader.component'
import { payoutAgreementActions } from '../../../../store/payout-agreement/payout-agreement.actions'
import {
  selectPayout,
  selectPayoutAgreementState
} from '../../../../store/payout-agreement/payout-agreement.reducers'
import { PayoutTabComponent } from '../payout-tab/payout-tab.component'
import { StrictEmailDirective } from '../../../../directives/strict-email.directive'
import { EmployeeResignationService } from '../../../../services/employee-resignation.service'
import { updateEmployee$ } from '../../../../store/user/user.effects'
import { AssignedPermissionSetsService } from '../../../../services/assigned-permissionsets.service'

export enum MaritalStatus {
  SINGLE = 'Single',
  MARRIED = 'Married',
  DIVORCED = 'Divorced',
  SEPARAETED = 'Separated',
  WIDOWED = 'Widowed'
}

export enum Religion {
  HINDUISM = 'Hinduism',
  BUDDHISM = 'Buddhism',
  JAINISM = 'Jainism',
  SIKHISM = 'Sikhism',
  CHRISTIANITY = 'Christianity',
  ISLAM = 'Islam',
  OTHER = 'Other'
}

export enum MemberType {
  SELF = 'Self',
  FAMILY = 'Family'
}
type PermissionAction = 'status' | 'view' | 'add' | 'edit' | 'delete'

type Permissions = Partial<Record<PermissionAction, boolean>>

export enum Relation {
  FATHER = 'Father',
  MOTHER = 'Mother',
  SPOUSE = 'Spouse',
  SIBLING = 'Sibling',
  SON = 'Son',
  DAUGHTER = 'Daughter',
  OTHER = 'Other'
}

export enum OffBoardingType {
  RETIREMENT = 'Retirement',
  RESIGNATION = 'Resignation',
  TERMINATION = 'Termination',
  LAYOFF = 'Layoff',
  CONTRACT = 'Contract',
  SEASONAL = 'Seasonal'
}

@Component({
  selector: 'app-employee-detail',
  imports: [
    ReactiveFormsModule,
    NgIf,
    CommonModule,
    RouterModule,
    CapitalizePipe,
    BlockUnblockComponent,
    FormsModule,
    DeleteComponent,
    PaginationComponent,
    HasPermissionDirective,
    SharedModule,
    CapitalizePipe,
    NgSelectModule,
    NgbDatepickerModule,
    GenerateLogMessagePipe,
    LoaderComponent,
    StrictEmailDirective,
    PayoutTabComponent
  ],
  templateUrl: './employee-detail.component.html',
  styleUrls: ['./employee-detail.component.scss', './employee-detail-accordian.component.scss']
})
export class EmployeeDetailComponent implements OnInit, OnDestroy {
  private store = inject(Store) // Inject store using `inject()`
  private attendancePolicyService = inject(AttendancePolicyService)
  private leavePolicyService = inject(LeavePolicyService)
  private bankDetailService = inject(BankService)
  private assignedPermissionSetsService = inject(AssignedPermissionSetsService)
  eps = eps //EndPoints from URL Service
  public defaultImg = '../../../../assets/img/user.jpg'
  public imagePath: any
  public imageURL: string | null = this.defaultImg
  public apiEndpoint = environment.apiBaseUrl
  employeeId = ''
  employee$: Observable<User | undefined> = of(undefined) // Initialize with a default value
  employeeImages: { [key: string]: string } = {} // Ensure this is defined correctly
  personalform!: FormGroup
  private modalRef!: NgbModalRef
  maritalStatusEnum = MaritalStatus // Make the enum accessible in the template
  religionEnum = Religion
  memberTypeEnum = MemberType
  relationEnum = Relation
  // families$!: Observable<Family[]>
  families$ = this.store.select(selectFamilies) // or however you get it
  @ViewChild('addBusinessModal') addBusinessModal!: TemplateRef<any>
  selfMember$ = this.families$.pipe(map(families => families.find(f => f.memberType === 'Self')))
  selfMember!: Family | null
  familyMembers$ = this.families$.pipe(
    map(families => families.filter(f => f.memberType === 'Family'))
  )
  // familyMembers!: Family[] | null

  familyform!: FormGroup
  public today: Date = new Date()
  public todayDate = `${this.today.getUTCFullYear()}-${this.today.getUTCDay() + 1 < 10 ? '0' : ''}${this.today.getUTCDay() + 1}-${this.today.getUTCDate()}`
  public todayplussixmonths: any = dayjs().add(6, 'month').endOf('day')

  public maturedDate: Date = new Date()
  employeeMemberId: any
  deleteRecordId: any
  showDeleteModal = false
  bankForm!: FormGroup
  bankId: any
  // banks$ = this.store.select(selectBanks) // or however you get it
  banks$ = this.store
    .select(selectBanks)
    .pipe(map(banks => [...banks].sort((a, b) => Number(b.isDefault) - Number(a.isDefault))))

  experienceForm!: FormGroup
  experienceId: any
  experiences$ = this.store.select(selectExperiences) // or however you get it

  educationForm!: FormGroup
  educationId: any
  educations$ = this.store.select(selectEducations) // or however you get it

  allSalaries$ = this.store.select(selectAllSalaries)
  employeeSalaryDetails: EmployeeSalary | null = null
  salaryCompliancesList!: StatutoryCompliances
  salaryDataArr: any

  payout$ = this.store.select(selectPayout)

  emergencyContactform!: FormGroup
  form!: FormGroup
  currentStep = 1
  steps = [1, 2, 3] // Steps for the form
  countries$: Observable<Country[]> | undefined
  states$: Observable<CityCountryState[]> | undefined
  cities$: Observable<City[]> | undefined
  businesses$: Observable<Business[]> | undefined
  offices$: Observable<Office[]> | undefined
  selectedCountryId = ''
  selectedStateId = ''
  officeId: string | null = null
  maxDate = ''
  designations$: Observable<Designation[]> = this.store.select(selectDesignations)
  departments$: Observable<Department[]> = this.store.select(selectDepartments)
  selectedBusinessId = ''
  selectedDepartmentId = ''
  //---Assigned Attendance and Leave Policies-----
  attendancePolicies$: any = [] //Assigned Attendance Policies
  selectedAttendancePolicy: any = {}
  leavePolicies$: any = [] //Assigned Leave Policies
  selectedLeavePolicy: any = {}
  //---Permission sets and assigned permission sets---
  roles$: Observable<Role[]> = this.store.select(selectRoles)
  permissionSets$: Observable<Role[]> = this.store.select(selectRoles)
  assignedPermissionSets$ = this.store.select(selectAssignedPermissionSets)
  assignPermissionSetsForm!: FormGroup
  assignedPermissionSets: any = []
  permissionSetsList: Role[] = []

  selectedAvailable: string[] = [] // Selected items from available list
  selectedAssigned: string[] = [] // Selected items from assigned list
  viewAssignedRole: any = ''
  private destroyed$ = new Subject<void>()
  user: AuthState['user'] | null = null
  businessIdss!: string[]
  businessId = ''
  showModal = false
  isBlocking!: boolean // Assuming this will be set before opening the modal
  blockEmployeeId = ''
  tasklogs$ = this.store.select(selectTasklogs) // or however you get it
  currentPage = 1
  currentTasklogPage: number = 1
  totalTasklogCount$!: Observable<number>
  totalTasklogCount: number = 0
  pageLastRecord: number = 0
  pageTasklogLastRecord: number = 0
  totalExperienceYears$!: Observable<number>
  totalExperienceYears: number = 0
  title = ''
  desc = ''
  @ViewChild('assignManagerModal') assignManagerModal!: TemplateRef<any>
  users$!: Observable<User[]>
  filteredUsers$!: Observable<User[]>
  private isFormInitialized = false
  @ViewChild('offBoardAccordion')
  offBoardAccordion!: TemplateRef<any>

  assignManagerform!: FormGroup
  isLoading$ = this.store.pipe(select(selectLoading))
  bankloading = false
  selectedYear: number = new Date().getFullYear()
  years: number[] = []
  assignRoleform!: FormGroup
  manager = ''
  requestManager = ''
  hrManager = ''
  changePasswordForm: FormGroup
  offBoardForm: FormGroup
  isSubmitting = false
  Math = Math
  offboardingList: any[] = []
  offBoardId: any = ''
  offBoardingEnum = OffBoardingType
  originalFormValue: any
  modalTitle: any

  permissionModules: {
    key: string
    name: string
    permissions: PermissionAction[]
  }[] = [
    {
      key: 'business',
      name: 'Business Entity',
      permissions: ['view', 'add', 'edit', 'delete']
    },
    { key: 'office', name: 'Office', permissions: ['view', 'add', 'edit', 'delete'] },
    { key: 'designation', name: 'Designation', permissions: ['view', 'add', 'edit', 'delete'] },
    { key: 'department', name: 'Department', permissions: ['view', 'add', 'edit', 'delete'] },
    // { key: 'user', name: 'User', permissions: ['view', 'add', 'edit', 'delete'] },
    { key: 'holidays', name: 'Holidays', permissions: ['view', 'add', 'edit', 'delete'] },
    { key: 'leaves', name: 'Leaves', permissions: ['view', 'add', 'edit', 'delete'] },
    {
      key: 'leaveRequests',
      name: 'Leave Requests',
      permissions: ['view', 'add', 'edit', 'delete']
    },
    { key: 'jobs', name: 'Jobs', permissions: ['view', 'add', 'edit', 'delete'] },
    { key: 'candidate', name: 'Candidate', permissions: ['view', 'add', 'edit', 'delete'] },
    { key: 'attendance', name: 'Attendance', permissions: ['view', 'add', 'edit', 'delete'] },
    { key: 'userRole', name: 'Role & Permission', permissions: ['view', 'add', 'edit', 'delete'] },
    {
      key: 'attendancePolicy',
      name: 'Attendance Policy',
      permissions: ['view', 'add', 'edit', 'delete']
    },
    { key: 'leavePolicy', name: 'Leave Policy', permissions: ['view', 'add', 'edit', 'delete'] },
    {
      key: 'attendanceList',
      name: 'Attendance List',
      permissions: ['view', 'add', 'edit', 'delete']
    },
    {
      key: 'standardEarnings',
      name: 'Standard Earnings',
      permissions: ['view', 'add', 'edit', 'delete']
    },
    {
      key: 'customEarnings',
      name: 'Custom Earnings',
      permissions: ['view', 'add', 'edit', 'delete']
    },
    {
      key: 'customDeductions',
      name: 'Custom Deductions',
      permissions: ['view', 'add', 'edit', 'delete']
    },
    {
      key: 'StatutoryDeductions',
      name: 'Statutory Deductions',
      permissions: ['view', 'add', 'edit', 'delete']
    },
    {
      key: 'reimbursements',
      name: 'Reimbursements',
      permissions: ['view', 'add', 'edit', 'delete']
    },
    {
      key: 'employeeSalary',
      name: 'Employee Salary',
      permissions: ['view', 'add', 'edit', 'delete']
    }

    // Add more modules as needed
  ]
  permissionsState: {
    [key: string]: Partial<Record<PermissionAction, boolean>>
  } = {}
  leavePolicies: any[] = [] // use a more specific type if available
  pageLimit: number = 10
  employeeData: any = ''
  employeeBusinessId = ''
  showView = false
  activeTab: string = 'attendance'
  selectedSalaryId: string | null | undefined = null
  constructor(
    private authService: AuthService,
    private modalService: NgbModal,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private toasterNotification: ToastrService,
    private actions$: Actions,
    private location: Location,
    private userService: UserService,
    private empResignationService: EmployeeResignationService
  ) {
    localStorage.removeItem('currentCtc')
    localStorage.removeItem('salaryCalculatedCtc')
    // this.employeeId = this.route.snapshot.paramMap.get('id')!
    // this.employee$ = this.store.select(selectEmployeeDetail).pipe(
    //   map(employee => {
    //     if (employee) {
    //       this.employeeData = employee
    //       if (employee?.picture) {
    //         this.imageURL = `${this.apiEndpoint}/uploads/${employee.picture}`
    //       } else {
    //         this.imageURL = null // fallback to placeholder icon
    //       }
    //       if (employee?.userRoles?.some(r => r?.role?.name === 'Contractor')) {
    //         this.store.dispatch(
    //           payoutAgreementActions.loadPayoutAgreement({ employeeId: this.employeeId })
    //         )
    //       } else {
    //         this.store.dispatch(
    //           loadSalary({ employeeId: this.employeeId, belongsTo: employee.businessId })
    //         )
    //         this.store.dispatch(
    //           loadAllSalaries({ employeeId: this.employeeId, belongsTo: employee.businessId })
    //         )
    //       }
    //     }
    //     if (employee && typeof employee.emergencyContact === 'string') {
    //       try {
    //         return {
    //           ...employee,
    //           emergencyContact: JSON.parse(employee.emergencyContact)
    //         }
    //       } catch (e) {
    //         console.error('Invalid emergencyContact JSON:', e)
    //         return employee
    //       }
    //     }
    //     return employee
    //   })
    // )
    this.employee$ = this.store.select(selectEmployeeDetail).pipe(
      filter(employee => !!employee), // only valid values
      tap(employee => {
        this.employeeData = employee

        if (employee?.picture) {
          this.imageURL = `${this.apiEndpoint}/uploads/${employee.picture}`
        } else {
          this.imageURL = this.defaultImg
        }
      }),
      map(employee => {
        // pure transformation only
        if (typeof employee.emergencyContact === 'string') {
          try {
            return {
              ...employee,
              emergencyContact: JSON.parse(employee.emergencyContact)
            }
          } catch (e) {
            console.error('Invalid emergencyContact JSON:', e)
            return employee
          }
        }
        return employee
      }),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
    )
    // this.loadOffboarding()
    // const params = this.employeeId ? `userId=${this.employeeId}` : null
    // side effects ONLY here
    // const isContractor = this.employeeData?.userRoles?.some(
    //   (r: { role: { name: string } }) => r?.role?.name === 'Contractor'
    // )

    // if (isContractor) {
    //   this.store.dispatch(
    //     payoutAgreementActions.loadPayoutAgreement({
    //       employeeId: this.employeeId
    //     })
    //   )
    // } else {
    //   this.store.dispatch(
    //     loadSalary({ employeeId: this.employeeId, belongsTo: this.employeeData?.businessId })
    //   )
    //   this.store.dispatch(
    //     loadAllSalaries({ employeeId: this.employeeId, belongsTo: this.employeeData?.businessId })
    //   )
    // }
    combineLatest([
      this.store.select(selectRoles).pipe(filter(roles => roles && roles.length > 0)),
      this.store.select(selectAssignedPermissionSets).pipe(filter(sets => sets !== null))
    ])
      .pipe(take(1))
      .subscribe(([roles, assignedPermissionSets]) => {
        // this.permissionSetsList = roles
        this.permissionSetsList = roles.filter(role => role.roleType === 'Permission Sets')

        this.selectedAvailable = []
        if (assignedPermissionSets && assignedPermissionSets.length > 0) {
          assignedPermissionSets.forEach(d => {
            this.selectedAvailable.push(d.userId)
          })
          this.moveToAssigned()
        }
      })

    this.store.dispatch(loadCountries())
    // const params = this.search ? `name=${encodeURIComponent(this.search)}` : null
    // this.store.dispatch(loadDesignations({ params: null }))
    // this.store.dispatch(loadDepartments({ params: null }))

    this.totalTasklogCount$ = this.store.select(selectTotalCount).pipe(
      map(totalCount => totalCount || 0) // Default to 0 if totalCount is null
    )

    this.totalExperienceYears$ = this.store.select(selectTotalExperienceYears).pipe(
      map(totalCount => totalCount || 0) // Default to 0 if totalCount is null
    )

    this.changePasswordForm = this.fb.group(
      {
        oldPassword: ['', Validators.required],
        newPassword: ['', [Validators.required, Validators.minLength(6)]],
        confirmPassword: ['', Validators.required]
      },
      { validators: this.passwordsMatchValidator }
    )

    this.offBoardForm = this.fb.group({
      date: ['', Validators.required],
      reason: ['', [Validators.required, Validators.minLength(6)]],
      effectiveLastDay: ['', Validators.required],
      offboardingType: ['', Validators.required],
      isSalaryHold: [false, Validators.required],
      salaryHoldFrom: [''],
      salaryHoldTo: [''],
      isFullNFinalRequired: [false],
      fullNFinalDate: ['']
    })
  }

  ngOnInit() {
    // this.employeeId = this.route.snapshot.paramMap.get('id')!
    // this.store.dispatch(loadEmployeeById({ id: this.employeeId }))
    this.countries$ = this.store.select(selectAllCountries)
    this.route.paramMap
      .pipe(
        map(params => params.get('id')!),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe(id => {
        const isFirstLoad = !this.employeeId
        this.employeeId = id
        this.isFormInitialized = false // allow form to repopulate for the new employee
        this.store.dispatch(loadEmployeeById({ id: this.employeeId }))
        this.loadOffboarding()

        this.currentTasklogPage = 1
        if (!isFirstLoad) {
          this.loadTasklogs()
        }

        const params = `userId=${encodeURIComponent(this.employeeId)}`
        this.store.dispatch(loadFamilies({ params }))
        this.store.dispatch(loadExperiences({ params }))

        this.attendancePolicies$ = this.attendancePolicyService.assignedPolicyByUserId(
          this.employeeId
        )
        this.leavePolicies$ = this.leavePolicyService.assignedPolicyByUserId(this.employeeId)
        const currentTab = this.route.snapshot.queryParams['tab']
        if (currentTab) {
          this.activeTab = currentTab
        }
        if (this.activeTab === 'leave') {
          this.loadLeavePolicies()
        }
        // Skip on first load — ngOnInit's own setup below will handle it once
        // (forms/observables aren't built yet on the very first emission).
        // On subsequent emissions (navigating employee -> my-profile etc.),
        // everything is already initialized, so it's safe to re-sync here.
        if (!isFirstLoad) {
          this.assignRoleform?.get('userId')?.setValue(this.employeeId)
          this.assignManagerform?.get('userId')?.setValue(this.employeeId)
          this.personalform?.get('userId')?.setValue(this.employeeId)
          this.getAssignManager()
        }
      })
    // this.store.dispatch(
    //   loadAssignedPermissionSets({
    //     id: this.employeeId,
    //     page: this.currentPage,
    //     limit: this.pageLimit
    //   })
    // )
    // Subscribe to the observable and populate assignedPermissionSets
    this.assignedPermissionSets$.subscribe(data => {
      // If the store selector returns { assignedPermissionSets: [...], totalCount: ... }
      this.assignedPermissionSets = data || this.user?.userPermissionSets || []
    })
    this.maturedDate.setFullYear(this.today.getFullYear() - 18)
    this.maxDate = this.maturedDate.toISOString().split('T')[0] // Format as 'yyyy-MM-dd'

    this.route.queryParams.subscribe(params => {
      this.activeTab = params['tab'] || 'attendance'
    })

    setTimeout(() => {
      this.showView = true
    }, 500) // 1000 ms = 1 seconds

    this.form = this.fb.group({
      basicInfoForm: this.fb.group({
        firstName: ['', [Validators.required, Validators.maxLength(50)]],
        lastName: ['', [Validators.required, Validators.maxLength(50)]],
        email: ['', [Validators.required, Validators.email]],
        personalEmail: ['', this.conditionalEmailValidator()],
        // password: ['', [Validators.required, Validators.minLength(6)]],
        // cnfpassword: [null, [Validators.required]],
        mobile: ['', [Validators.required, this.mobileNumberValidator()]],
        personalMobile: ['', this.conditionalMobileValidator()],
        birthdate: ['', [this.ageValidator(18)]],
        gender: ['', Validators.required],
        about: [''],
        role: 'Employee',
        picture: ['']
      }),
      companyInfoForm: this.fb.group({
        joiningDate: ['', Validators.required],
        // website: ['', [Validators.required, Validators.pattern(
        //   '^(https?:\\/\\/)?(www\\.)?[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}(:[0-9]+)?(\\/.*)?$'
        // )]],
        // fax: ['', Validators.required],
        businessId: ['', Validators.required],
        officeId: ['', Validators.required],
        departmentId: ['', Validators.required],
        designationId: ['', Validators.required],
        empId: ['', Validators.required]
      }),
      addressInfoForm: this.fb.group({
        address: ['', Validators.required],
        cityId: ['', Validators.required],
        state_id: ['', Validators.required],
        country_id: ['', Validators.required],
        zipCode: ['', Validators.required]
      }),
      socialInfoForm: this.fb.group({
        referenceIdFacebook: [
          '',
          [
            // Validators.required,
            Validators.pattern(
              '^(https?:\\/\\/)(www\\.)?[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}(:[0-9]+)?(\\/.*)?$'
            )
          ]
        ],
        referenceIdTwitter: [
          '',
          [
            // Validators.required,
            Validators.pattern(
              '^(https?:\\/\\/)(www\\.)?[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}(:[0-9]+)?(\\/.*)?$'
            )
          ]
        ],
        referenceIdLinkedIn: [
          '',
          [
            // Validators.required,
            Validators.pattern(
              '^(https?:\\/\\/)(www\\.)?[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}(:[0-9]+)?(\\/.*)?$'
            )
          ]
        ],
        // referenceIdSkype: [
        //   '',
        //   [
        //     Validators.required,
        //     Validators.pattern(
        //   '^(https?:\\/\\/)?(www\\.)?[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}(:[0-9]+)?(\\/.*)?$'
        // )

        //   ]
        // ],
        referenceIdWhatsapp: [
          '',
          [
            // Validators.required,
            Validators.pattern(
              '^(https?:\\/\\/)(www\\.)?[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}(:[0-9]+)?(\\/.*)?$'
            )
          ]
        ],
        referenceIdInstagram: [
          '',
          [
            // Validators.required,
            Validators.pattern(
              '^(https?:\\/\\/)(www\\.)?[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}(:[0-9]+)?(\\/.*)?$'
            )
          ]
        ]
      }),
      rolepermissionInfoForm: this.fb.group({
        userRoleId: [''],
        permissions: ['']
      })
    })

    this.assignRoleform = this.fb.group({
      roleId: ['', Validators.required],
      userId: this.employeeId
    })

    this.assignManagerform = this.fb.group({
      managerId: [''],
      reqManagerId: [''],
      hrManagerId: [''],
      userId: this.employeeId
    })

    this.employee$.pipe(takeUntil(this.destroyed$)).subscribe(employee => {
      const reqManagerControl = this.assignManagerform.get('reqManagerId')

      if (employee?.employmentType === 'Business Owner') {
        reqManagerControl?.clearValidators()
      } else {
        reqManagerControl?.setValidators([Validators.required])
      }

      reqManagerControl?.updateValueAndValidity()
    })

    this.actions$
      .pipe(ofType(portalAccessEmployeeSuccess, blockEmployeeSuccess), takeUntil(this.destroyed$))
      .subscribe(() => {
        this.store.dispatch(loadEmployeeById({ id: this.employeeId }))
      })

    // this.employee$.subscribe(employee => {
    // combineLatest([this.employee$, this.selfMember$]).subscribe(([employee, selfMember]) => {
    //   if (employee) {
    //     this.employeeBusinessId = employee.businessId
    //     this.selfMember = selfMember ?? null
    //     this.assignedPermissionSets = employee.userPermissionSets
    //     // const rolesparams = `roleType=Permission Sets&businessId=${employee.businessId}`
    //     const rolesparams = `businessId=${employee.businessId}`

    //     this.store.dispatch(loadRoles({ params: rolesparams }))
    //     this.assignRoleform?.get('roleId')?.setValue(employee.userRole?.id)

    //     // if (employee.picture) {
    //     //   this.businessImage(employee.picture)
    //     // }
    //     if (!this.isFormInitialized) {
    //       this.isFormInitialized = true
    //       this.form.patchValue({
    //         basicInfoForm: {
    //           firstName: employee.firstName,
    //           lastName: employee.lastName,
    //           email: employee.email,
    //           personalEmail: employee.personalEmail,
    //           mobile: employee.mobile,
    //           personalMobile: employee.personalMobile,
    //           birthdate: employee.birthdate,
    //           gender: employee.gender,
    //           about: employee.about,
    //           role: employee.role,
    //           picture: employee.picture
    //         },
    //         companyInfoForm: {
    //           joiningDate: employee.joiningDate,
    //           businessId: employee.businessId,
    //           officeId: employee.officeId ?? null,
    //           departmentId: employee.designation ? employee.designation.departmentId : '',
    //           designationId: employee.designationId,
    //           empId: employee.empId
    //         },
    //         addressInfoForm: {
    //           // address: this.selfMember?.address || '',
    //           // cityId: this.selfMember?.city?.id || null,
    //           // state_id: this.selfMember?.city?.state_id || null,
    //           // country_id: this.selfMember?.city?.country_id || null,
    //           // zipCode: this.selfMember?.zipCode || ''
    //           address: selfMember?.address || '',
    //           cityId: selfMember?.city?.id || null,
    //           state_id: selfMember?.city?.state_id || null,
    //           country_id: selfMember?.city?.country_id || null,
    //           zipCode: selfMember?.zipCode || ''
    //         },
    //         socialInfoForm: {
    //           referenceIdFacebook: employee.referenceIdFacebook,
    //           referenceIdTwitter: employee.referenceIdTwitter,
    //           referenceIdLinkedIn: employee.referenceIdLinkedIn,
    //           // referenceIdSkype: employee.referenceIdSkype,
    //           referenceIdWhatsapp: employee.referenceIdWhatsapp,
    //           referenceIdInstagram: employee.referenceIdInstagram
    //         },
    //         rolepermissionInfoForm: {
    //           userRoleId: employee.userRole?.id
    //           // permissions: employee.permissions
    //         }
    //       })
    //       this.originalFormValue = this.form.getRawValue()
    //       // if (this.selfMember && this.selfMember.city) {
    //       if (selfMember && selfMember.city) {
    //         // this.form.get('addressInfoForm.country_id')?.setValue(this.selfMember.city.country_id)
    //         this.form.get('addressInfoForm.country_id')?.setValue(selfMember.city.country_id)
    //         // this.onCountrySelect(this.selfMember.city.country_id.toString())
    //         this.onCountrySelect(selfMember.city.country_id.toString())
    //       }
    //       // // if (this.selfMember && this.selfMember.city) {
    //       if (selfMember && selfMember.city) {
    //         // this.onStateSelect(this.selfMember.city.state_id.toString())
    //         this.onStateSelect(selfMember.city.state_id.toString())
    //       }
    //       this.form.get('companyInfoForm.businessId')?.setValue(employee.businessId)
    //       this.onBusinessSelect(employee.businessId)
    //       this.form.get('companyInfoForm.officeId')?.setValue(employee.officeId)
    //       this.form
    //         .get('companyInfoForm.departmentId')
    //         ?.setValue(employee.designation?.departmentId)
    //       if (employee.designation) {
    //         this.onDepartmentSelect(employee.designation.departmentId)
    //         this.form.get('companyInfoForm.designationId')?.setValue(employee.designationId)
    //       }
    //     }
    //   }
    // })

    // this.store
    //   .select(selectEmployeeSalary)
    //   .pipe(
    //     filter(data => !!data),
    //     takeUntil(this.destroyed$)
    //   )
    //   .subscribe(data => {
    //     if (data === null) return
    //     this.employeeSalaryDetails = data
    //     this.salaryCompliancesList = this.employeeSalaryDetails?.statutoryCompliances
    //     // this.salaryDataArr = Object.entries(this.employeeSalaryDetails?.salaryData || {})
    //     //   .filter(([key, value]) => !(key === 'pf' && value === 0))
    //     //   .map(([key, value]) => {
    //     //     if (['hra'].includes(key)) {
    //     //       key = key.toUpperCase()
    //     //     } else if (key === 'specialAllowance') {
    //     //       key = 'Special Allowances'
    //     //     } else if (key === 'pf') {
    //     //       key = "Employee's PF Contribution"
    //     //     } else if (key === 'basic') {
    //     //       key = 'Basic'
    //     //     }

    //     //     return [key, value]
    //     //   })

    //     // .sort((a, b) => a[0].localeCompare(b[0]))

    //     //before 20 may 2026
    //     // this.salaryDataArr = Object.entries(this.employeeSalaryDetails?.salaryData || {})
    //     //   .filter(([key, value]) => !(key === 'pf' && value === 0))
    //     //   .flatMap(([key, value]: any) => {
    //     //     // ✅ Handle otherComponents separately
    //     //     if (key === 'otherComponents' && Array.isArray(value)) {
    //     //       return value.map((item: any) => {
    //     //         // const isESIOld = item.name === 'ESI Old'
    //     //         const isESIOld = /esi\s*[-_ ]*old/i.test(item.name)

    //     //         const label = isESIOld
    //     //           ? `${item.name} - Employer Contribution`
    //     //           : item.name || 'Other Component'

    //     //         const val = item.employerContribution || item.amount || 0

    //     //         return {
    //     //           label,
    //     //           value: val,
    //     //           isEmployer: isESIOld // 👈 flag for ordering
    //     //         }
    //     //       })
    //     //     }

    //     //     let label = key

    //     //     if (key === 'hra') label = 'HRA'
    //     //     else if (key === 'specialAllowance') label = 'Special Allowance'
    //     //     else if (key === 'pf') label = "Employee's PF Contribution"
    //     //     else if (key === 'basic') label = 'Basic'

    //     //     return [
    //     //       {
    //     //         label,
    //     //         value,
    //     //         isEmployer: false
    //     //       }
    //     //     ]
    //     //   })

    //     //   // ✅ SORT LOGIC: employer components LAST
    //     //   .sort((a: any, b: any) => {
    //     //     return Number(a.isEmployer) - Number(b.isEmployer)
    //     //   })
    //     //   .map(({ label, value }: any) => [label, value])

    //     // 20 may 2026
    //     this.salaryDataArr = Object.entries(this.employeeSalaryDetails?.salaryData || {})
    //       .filter(([key, value]) => !(key === 'pf' && value === 0))

    //       .flatMap(([key, value]: any) => {
    //         // ✅ Handle otherComponents separately
    //         if (key === 'otherComponents' && Array.isArray(value)) {
    //           return (
    //             value

    //               // ✅ Only custom deductions + NOT included in CTC
    //               .filter((item: any) => {
    //                 const type = (
    //                   item?.componentType ||
    //                   item?.info?.componentType ||
    //                   item?.info?.componenttype ||
    //                   ''
    //                 )
    //                   .toString()
    //                   .replace(/\s|_/g, '')
    //                   .toLowerCase()

    //                 return (
    //                   ['customdeduction', 'customdeductions', 'customDeductions'].includes(type) &&
    //                   item?.info?.isInCTC === false
    //                 )
    //               })

    //               .map((item: any) => {
    //                 // ✅ Match:
    //                 // ESI
    //                 // ESI OLD
    //                 // ESI - OLD
    //                 // ESI_old
    //                 // etc.

    //                 const isESI = /^esi\b/i.test(item?.name || '')

    //                 const label = isESI
    //                   ? `${item.name} - Employer Contribution`
    //                   : item.name || 'Other Component'

    //                 const val = Number(item.employerContribution || 0) || Number(item.amount || 0)

    //                 return {
    //                   label,
    //                   value: val,
    //                   isEmployer: isESI
    //                 }
    //               })
    //           )
    //         }

    //         // ✅ Default salary fields
    //         let label = key

    //         if (key === 'hra') label = 'HRA'
    //         else if (key === 'specialAllowance') label = 'Special Allowance'
    //         else if (key === 'pf') label = "Employee's PF Contribution"
    //         else if (key === 'basic') label = 'Basic'

    //         return [
    //           {
    //             label,
    //             value,
    //             isEmployer: false
    //           }
    //         ]
    //       })

    //       // ✅ Employer components at bottom
    //       .sort((a: any, b: any) => {
    //         return Number(a.isEmployer) - Number(b.isEmployer)
    //       })

    //       // ✅ Final format
    //       .map(({ label, value }: any) => [label, value])
    //   })

    this.personalform = this.fb.group({
      passport: [''],
      passportExpiryDate: [''],
      aadharNo: ['', [Validators.required, Validators.maxLength(19)]],
      PAN: ['', Validators.required],
      nationality: ['', Validators.required],
      religion: ['', Validators.required],
      maritalStatus: ['', Validators.required],
      isSpouseEmployed: [false, Validators.required],
      noOfChildren: [0],
      userId: this.employeeId,
      pfUAN: [
        '',
        [
          Validators.maxLength(14) // 12 digits + 2 hyphens
        ]
      ],
      esiNumber: [
        '',
        [
          Validators.maxLength(23) // 17 digits + 4 hyphens
        ]
      ]

      // Add other form controls as needed
    })

    this.familyform = this.fb.group({
      name: ['', Validators.required],
      relation: ['', Validators.required],
      birthdate: [''],
      mobile: ['', this.conditionalMobileValidator()]
    })

    // this.bankForm = this.fb.group({
    //   bank: ['', [Validators.required, Validators.maxLength(50)]],
    //   branch: ['', [Validators.required, Validators.maxLength(50)]],
    //   ifsc: ['', [Validators.required, Validators.maxLength(50)]],
    //   accountNo: ['', [Validators.required, Validators.maxLength(50)]]
    // })

    this.bankForm = this.fb.group({
      bank: ['', [Validators.required, Validators.maxLength(50)]],
      branch: [
        '',
        [Validators.required, Validators.maxLength(50), Validators.pattern('^[a-zA-Z0-9\\s\\-]+$')]
      ],
      ifsc: ['', [Validators.required, Validators.pattern('^[A-Z]{4}0[A-Z0-9]{6}$')]],
      accountNo: ['', [Validators.required, Validators.pattern('^[0-9]{9,18}$')]]
    })

    this.emergencyContactform = this.fb.group({
      nameP: ['', [Validators.required, Validators.maxLength(50)]],
      relationshipP: ['', [Validators.required, Validators.maxLength(50)]],
      phoneP: ['', [Validators.required, this.mobileNumberValidator()]],
      phone2P: [''],
      // nameS: ['', [Validators.required, Validators.maxLength(50)]],
      // relationshipS: ['', [Validators.required, Validators.maxLength(50)]],
      // phoneS: ['', [Validators.required, this.mobileNumberValidator()]],
      phone2S: ['']
    })

    this.educationForm = this.fb.group(
      {
        university: ['', [Validators.required, Validators.maxLength(100)]],
        degree: ['', [Validators.required, Validators.maxLength(100)]],
        startDate: ['', Validators.required],
        endDate: ['', Validators.required]
      },
      { validators: this.dateRangeValidator('startDate', 'endDate', '') }
    )

    this.experienceForm = this.fb.group(
      {
        company: ['', [Validators.required, Validators.maxLength(100)]],
        designation: ['', [Validators.required, Validators.maxLength(100)]],
        startDate: ['', Validators.required],
        endDate: [''],
        isPresent: [false] // Initialize as unchecked
      },
      { validators: this.dateRangeValidator('startDate', 'endDate', 'isPresent') }
    )

    this.selfMember$.subscribe(self => {
      this.selfMember = self ?? null
    })

    this.assignPermissionSetsForm = this.fb.group({
      permissionSetIds: [''] // Example validators
      // permissionSetIds: this.fb.array([], Validators.required)
    })

    this.actions$
      .pipe(
        ofType(createFamilySuccess, updateFamilySuccess),
        takeUntil(this.destroyed$) // don't forget to clean up
      )
      .subscribe(() => {
        this.familyform.reset()
        const params = this.employeeId ? `userId=${encodeURIComponent(this.employeeId)}` : null
        this.store.dispatch(loadFamilies({ params }))
        if (this.currentStep === 1) {
          this.modalService.dismissAll() // or modalRef.close() if you're using one
        }
        setTimeout(() => {
          this.originalFormValue = this.form.getRawValue()
        }, 300)
      })

    this.actions$
      .pipe(
        ofType(createEmployeeSuccess, updateEmployeeSuccess),
        takeUntil(this.destroyed$) // don't forget to clean up
      )
      .subscribe(() => {
        this.store.dispatch(loadEmployeeById({ id: this.employeeId }))

        if (this.user?.id === this.employeeId) {
          this.store.dispatch(loadUserById({ id: this.employeeId }))
        }
        if (this.currentStep === 4) {
          this.modalService.dismissAll() // or modalRef.close() if you're using one
        }

        if (this.emergencyContactform?.get('nameP')?.value) {
          this.modalService.dismissAll() // or modalRef.close() if you're using one
        }
      })

    this.store
      .select(selectUser)
      .pipe(take(1))
      .subscribe(user => {
        this.user = user
        const businessIds = user?.businessIds // your array of business IDs
        // const paramString = `ids=${businessIds?.join(',') ?? ''}`;
        if (businessIds && businessIds.length) {
          this.businessIdss = businessIds
          // const paramString = `businessIds=${businessIds.join(',')}`
          // this.store.dispatch(loadBusinesses({ params: paramString }))
        }
        // else {
        //   this.loadAllBusinesses() // Load all businesses initially
        //   // this.loadAllBusinessOwners()
        // }
        // if (user && user.role !== 'Admin') {
        if (user && !this.isAdmin) {
          this.store.dispatch(
            loadEmployees({
              params: `businessId=${encodeURIComponent(user.businessId)}&disabled=false`
            })
          )
        } else {
          this.store.dispatch(loadEmployees({ params: `disabled=false` }))
        }

        // this.loadAllBusinesses()
      })

    this.experienceForm.get('isPresent')?.valueChanges.subscribe(isPresent => {
      const endDateControl = this.experienceForm.get('endDate')

      if (isPresent) {
        endDateControl?.clearValidators()
        endDateControl?.setValue(null) // Clear the value
      } else {
        endDateControl?.setValidators([Validators.required])
      }

      endDateControl?.updateValueAndValidity()
    })

    this.actions$
      .pipe(
        ofType(createEducationSuccess, updateEducationSuccess, updateEducationFailure),
        takeUntil(this.destroyed$) // don't forget to clean up
      )
      // .subscribe(() => {
      //   this.educationForm.reset()
      //   this.modalService.dismissAll() // or modalRef.close() if you're using one
      // })
      .subscribe(() => {
        this.educationForm.reset()
        this.educationId = ''
        const params = this.employeeId ? `userId=${encodeURIComponent(this.employeeId)}` : null
        this.store.dispatch(loadEducations({ params }))
        this.modalService.dismissAll() // or modalRef.close() if you're using one
      })

    this.actions$
      .pipe(
        ofType(createExperienceSuccess, updateExperienceSuccess, updateExperienceFailure),
        takeUntil(this.destroyed$) // don't forget to clean up
      )
      // .subscribe(() => {
      //   this.experienceForm.reset()
      //   this.modalService.dismissAll() // or modalRef.close() if you're using one
      // })
      .subscribe(() => {
        this.experienceForm.reset()
        this.experienceId = ''
        const params = this.employeeId ? `userId=${encodeURIComponent(this.employeeId)}` : null
        this.store.dispatch(loadExperiences({ params }))
        this.modalService.dismissAll() // or modalRef.close() if you're using one
      })

    // this.leavePolicyService.assignedPolicyByUserId(this.employeeId).subscribe({
    //   next: policies => {
    //     this.leavePolicies = policies
    //   },
    //   error: err => {
    //     console.error('Error fetching policies:', err)
    //   }
    // })

    // this.totalTasklogCount$.subscribe(count => {
    //   this.totalTasklogCount = count
    //   this.updateTasklogPageLastRecord()
    // })
    this.totalTasklogCount$
      .pipe(takeUntil(this.destroyed$)) // destroy$ is a Subject you complete in ngOnDestroy
      .subscribe(count => {
        this.totalTasklogCount = count
        this.updateTasklogPageLastRecord()
      })

    this.totalExperienceYears$
      .pipe(takeUntil(this.destroyed$)) // destroy$ is a Subject you complete in ngOnDestroy
      .subscribe(count => {
        this.totalExperienceYears = count
      })

    this.actions$
      .pipe(
        ofType(createBankSuccess, updateBankSuccess),
        takeUntil(this.destroyed$) // don't forget to clean up
      )
      .subscribe(() => {
        this.bankloading = false
        this.bankForm.reset()
        this.modalService.dismissAll() // or modalRef.close() if you're using one

        const params = this.employeeId ? `userId=${encodeURIComponent(this.employeeId)}` : null

        this.store.dispatch(loadBanks({ params }))
      })

    // this.loadUsers()
    this.users$ = this.store.select(selectAllUsers)
    this.filteredUsers$ = this.users$.pipe(
      map(users => users.filter(user => user.id !== this.employeeId))
    )

    this.actions$
      .pipe(
        ofType(
          createAssignedPermissionSetsSuccess,
          updateAssignedPermissionSetsSuccess,
          createAssignedToSuccess
        ),
        takeUntil(this.destroyed$) // don't forget to clean up
      )
      .subscribe(() => {
        this.experienceForm.reset()
        this.modalService.dismissAll() // or modalRef.close() if you're using one
        this.store.dispatch(loadEmployeeById({ id: this.employeeId }))

        // const params = this.employeeId
        //   ? `userId=${encodeURIComponent(this.employeeId)}&roleType:Permission Sets`
        //   : null

        // this.store.dispatch(
        //   loadAssignedPermissionSets({
        //     params
        //     // page: this.currentPage,
        //     // limit: this.pageLimit
        //   })
        // )
      })

    this.roles$ = this.store
      .select(selectRoles)
      .pipe(map(roles => roles.filter(role => role.roleType === 'Role')))

    this.permissionSets$ = this.store
      .select(selectRoles)
      .pipe(map(roles => roles.filter(role => role.roleType === 'Permission Set')))

    this.getAssignManager()

    this.emergencyContactform.get('phone2P')?.valueChanges.subscribe(value => {
      const control = this.emergencyContactform.get('phone2P')
      if (value) {
        control?.setValidators([this.mobileNumberValidator()])
      } else {
        control?.clearValidators()
      }
      control?.updateValueAndValidity({ emitEvent: false })
    })

    this.emergencyContactform.get('phone2S')?.valueChanges.subscribe(value => {
      const control = this.emergencyContactform.get('phone2S')
      if (value) {
        control?.setValidators([this.mobileNumberValidator()])
      } else {
        control?.clearValidators()
      }
      control?.updateValueAndValidity({ emitEvent: false })
    })

    const currentYear = new Date().getFullYear()
    this.years = Array.from({ length: 50 }, (_, i) => currentYear - i) // Last 50 years

    this.actions$
      .pipe(
        ofType(deleteEducationSuccess, deleteEducationFailure),
        takeUntil(this.destroyed$) // don't forget to clean up
      )
      .subscribe(() => {
        const params = this.employeeId ? `userId=${encodeURIComponent(this.employeeId)}` : null
        this.store.dispatch(loadEducations({ params }))
        this.modalService.dismissAll() // or modalRef.close() if you're using one
      })

    this.actions$
      .pipe(
        ofType(deleteExperienceSuccess, deleteExperienceFailure),
        takeUntil(this.destroyed$) // don't forget to clean up
      )
      .subscribe(() => {
        const params = this.employeeId ? `userId=${encodeURIComponent(this.employeeId)}` : null
        this.store.dispatch(loadExperiences({ params }))
        this.modalService.dismissAll() // or modalRef.close() if you're using one
      })

    const params = this.employeeId ? `userId=${encodeURIComponent(this.employeeId)}` : null

    this.store.dispatch(loadFamilies({ params }))
    // this.store.dispatch(loadBanks({ params }))
    this.store.dispatch(loadExperiences({ params }))
    // this.store.dispatch(loadEducations({ params }))
    // this.store.dispatch(loadBusinesses({}))
    // this.loadTasklogs()

    // this.countries$ = this.store.select(selectAllCountries)
    // this.states$ = this.store.select(selectAllStates)
    // this.cities$ = this.store.select(selectAllCities)
    // this.businesses$ = this.store.select(selectAllBusinesses)
    // this.offices$ = this.store.select(selectOffices)
    //----Assigned Attendance and Leave Policies-----
    this.attendancePolicies$ = this.attendancePolicyService.assignedPolicyByUserId(this.employeeId)
    this.leavePolicies$ = this.leavePolicyService.assignedPolicyByUserId(this.employeeId)
    //----Roles and permissions-----
    // Setup permission sets list and assigned permission sets list
    // const rolesparams = `roleType=Permission Sets`
    // this.store.dispatch(loadRoles({ params: rolesparams }))

    // this.store.dispatch(
    //   loadAssignedPermissionSets({
    //     params
    //     // page: this.currentPage,
    //     // limit: this.pageLimit
    //   })
    // )

    this.offBoardForm.get('isSalaryHold')?.valueChanges.subscribe(hold => {
      const from = this.offBoardForm.get('salaryHoldFrom')
      const to = this.offBoardForm.get('salaryHoldTo')

      if (hold === true) {
        from?.setValidators([Validators.required])
        to?.setValidators([Validators.required])
      } else {
        from?.clearValidators()
        to?.clearValidators()
        from?.reset()
        to?.reset()
      }

      from?.updateValueAndValidity()
      to?.updateValueAndValidity()
    })
    this.offBoardForm.get('isFullNFinalRequired')?.valueChanges.subscribe(val => {
      const fullDate = this.offBoardForm.get('fullNFinalDate')

      if (val === true) {
        fullDate?.setValidators([Validators.required])
      } else {
        fullDate?.clearValidators()
        fullDate?.reset()
      }

      fullDate?.updateValueAndValidity()
    })
    this.assignRoleform?.get('userId')?.setValue(this.employeeId)
    this.assignManagerform?.get('userId')?.setValue(this.employeeId)
    this.personalform?.get('userId')?.setValue(this.employeeId)
    this.getAssignManager()
  }

  //Helper method to check if the employee has any salary compliances
  hasValidCompliances(): boolean {
    return (
      this.salaryCompliancesList &&
      Object.keys(this.salaryCompliancesList).some(
        key =>
          key !== 'employerCapping' &&
          this.salaryCompliancesList[key as keyof StatutoryCompliances] === true
      )
    )
  }

  get monthlyTotalEarnings(): number {
    if (!this.employeeSalaryDetails) return 0
    const monthlyBasic = this.employeeSalaryDetails.salaryData.basic / 12
    const monthlyHra = this.employeeSalaryDetails.salaryData.hra / 12
    const monthlySpecialAllowance = this.employeeSalaryDetails.salaryData.specialAllowance / 12
    const otherComponents = this.employeeSalaryDetails.salaryData.otherComponents
      .filter(item => item.componentType !== 'customDeductions')
      .map(item => (item.employerContribution ?? 0) / 12)
    let otherEarnings = 0
    if (otherComponents.length > 0) {
      otherEarnings = otherComponents.reduce((acc, curr) => acc + curr, 0)
    }
    return monthlyBasic + monthlyHra + monthlySpecialAllowance + otherEarnings
  }

  get monthlyTotalDeductions(): number {
    if (!this.employeeSalaryDetails) return 0
    const pf = this.employeeSalaryDetails.salaryData.pf / 12
    const otherComponents = this.employeeSalaryDetails.salaryData.otherComponents
      .filter(item => item.componentType === 'customDeductions')
      .map(item => (item.employeeContribution ?? 0) / 12)
    let otherDeductions = 0
    if (otherComponents.length > 0) {
      otherDeductions = otherComponents.reduce((acc, curr) => acc + curr, 0)
    }
    return pf + otherDeductions
  }

  get totalDaysInPayrollPeriod(): number {
    const today = dayjs()
    const effectiveFrom = dayjs(this.employeeSalaryDetails?.effectiveFrom, 'DD-MMM-YYYY')

    const totalDaysInMonth = today.daysInMonth()

    if (effectiveFrom.isSame(today, 'month') && effectiveFrom.isSame(today, 'year')) {
      const effectiveDay = effectiveFrom.date()
      return totalDaysInMonth - (effectiveDay - 1)
    } else return totalDaysInMonth
  }

  copyContent(email: string) {
    if (!email) {
      this.toasterNotification.error('ERROR', 'No email to copy!')
      return
    }

    if (navigator.clipboard) {
      // Modern way
      navigator.clipboard
        .writeText(email)
        .then(() => {
          this.toasterNotification.success('SUCCESS', 'Email copied!')
        })
        .catch(err => {
          this.toasterNotification.error('ERROR', err.message || 'Could not copy email.')
          console.error('Could not copy email: ', err)
        })
    } else {
      // Fallback for older browsers
      const textarea = document.createElement('textarea')
      textarea.value = email
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
        this.toasterNotification.success('SUCCESS', 'Email copied!')
      } catch (err) {
        this.toasterNotification.error('ERROR', 'Could not copy email.')
        console.error('Could not copy email: ', err)
      } finally {
        document.body.removeChild(textarea)
      }
    }
  }

  businessImage(picture: string) {
    const imageUrl = this.apiEndpoint + '/uploads/' + picture // Assuming this is the correct path
    this.authService.getImage(imageUrl).subscribe(
      imageBlob => {
        const objectURL = URL.createObjectURL(imageBlob)
        this.employeeImages[picture] = objectURL // Store the object URL
      },
      error => {
        console.error('Error fetching image:', error)
      }
    )
  }

  getEmployeeImage(employee: User): string {
    if (employee?.picture && this.employeeImages[employee.picture]) {
      return this.employeeImages[employee.picture]
    } else if (employee.picture) {
      const imageUrl = this.apiEndpoint + '/uploads/' + employee.picture // Assuming this is the correct path
      this.authService.getImage(imageUrl).subscribe(
        imageBlob => {
          const objectURL = URL.createObjectURL(imageBlob)
          this.employeeImages[employee.picture ?? this.defaultImg] = objectURL // Store the object URL
        },
        error => {
          console.error('Error fetching image:', error)
        }
      )
    }
    return this.defaultImg
  }

  openBusinessModal(content: TemplateRef<any>, options: any = {}, modalType?: string) {
    this.currentStep = 1
    this.bankForm.reset()
    this.educationForm.reset()
    this.experienceForm.reset()
    this.emergencyContactform.reset()

    this.modalRef = this.modalService.open(content, {
      scrollable: false,
      centered: true,
      size: 'lg',
      ...options
    })

    if (!options?.effectiveLastDay) {
      this.offBoardId = ''

      this.offBoardForm.reset({
        isSalaryHold: false,
        isFullNFinalRequired: false
      })
    }

    // console.log("options ",options);
    // if (content === this.addBusinessModal) {
    if (modalType === 'addBusinessModal') {
      this.loadAllBusinesses()
      this.countries$ = this.store.select(selectAllCountries)
      this.states$ = this.store.select(selectAllStates)
      this.cities$ = this.store.select(selectAllCities)
      this.businesses$ = this.store.select(selectAllBusinesses)
      this.offices$ = this.store.select(selectOffices)

      combineLatest([this.employee$, this.selfMember$])
        .pipe(take(1))
        .subscribe(([employee, selfMember]) => {
          if (employee) {
            this.employeeBusinessId = employee.businessId
            this.selfMember = selfMember ?? null
            this.assignedPermissionSets = employee.userPermissionSets
            // const rolesparams = `roleType=Permission Sets&businessId=${employee.businessId}`
            const rolesparams = `businessId=${employee.businessId}`

            this.store.dispatch(loadRoles({ params: rolesparams }))
            this.assignRoleform?.get('roleId')?.setValue(employee.userRole?.id)

            // if (employee.picture) {
            //   this.businessImage(employee.picture)
            // }
            // if (!this.isFormInitialized) {
            // this.isFormInitialized = true
            this.form.patchValue({
              basicInfoForm: {
                firstName: employee.firstName,
                lastName: employee.lastName,
                email: employee.email,
                personalEmail: employee.personalEmail,
                mobile: employee.mobile,
                personalMobile: employee.personalMobile,
                birthdate: employee.birthdate,
                gender: employee.gender,
                about: employee.about,
                role: employee.role,
                picture: employee.picture
              },
              companyInfoForm: {
                joiningDate: employee.joiningDate,
                businessId: employee.businessId,
                officeId: employee.officeId ?? null,
                departmentId: employee.designation ? employee.designation.departmentId : '',
                designationId: employee.designationId,
                empId: employee.empId
              },
              addressInfoForm: {
                // address: this.selfMember?.address || '',
                // cityId: this.selfMember?.city?.id || null,
                // state_id: this.selfMember?.city?.state_id || null,
                // country_id: this.selfMember?.city?.country_id || null,
                // zipCode: this.selfMember?.zipCode || ''
                address: selfMember?.address || '',
                cityId: selfMember?.city?.id || null,
                state_id: selfMember?.city?.state_id || null,
                country_id: selfMember?.city?.country_id || null,
                zipCode: selfMember?.zipCode || ''
              },
              socialInfoForm: {
                referenceIdFacebook: employee.referenceIdFacebook,
                referenceIdTwitter: employee.referenceIdTwitter,
                referenceIdLinkedIn: employee.referenceIdLinkedIn,
                // referenceIdSkype: employee.referenceIdSkype,
                referenceIdWhatsapp: employee.referenceIdWhatsapp,
                referenceIdInstagram: employee.referenceIdInstagram
              },
              rolepermissionInfoForm: {
                userRoleId: employee.userRole?.id
                // permissions: employee.permissions
              }
            })

            this.originalFormValue = this.form.getRawValue()
            // if (this.selfMember && this.selfMember.city) {
            if (selfMember && selfMember.city) {
              // this.form.get('addressInfoForm.country_id')?.setValue(this.selfMember.city.country_id)
              this.form.get('addressInfoForm.country_id')?.setValue(selfMember.city.country_id)
              // this.onCountrySelect(this.selfMember.city.country_id.toString())
              this.onCountrySelect(selfMember.city.country_id.toString())
            }
            // // if (this.selfMember && this.selfMember.city) {
            if (selfMember && selfMember.city) {
              // this.onStateSelect(this.selfMember.city.state_id.toString())
              this.onStateSelect(selfMember.city.state_id.toString())
            }
            this.form.get('companyInfoForm.businessId')?.setValue(employee.businessId)
            this.onBusinessSelect(employee.businessId)
            this.form.get('companyInfoForm.officeId')?.setValue(employee.officeId)
            this.form
              .get('companyInfoForm.departmentId')
              ?.setValue(employee.designation?.departmentId)
            if (employee.designation) {
              this.onDepartmentSelect(employee.designation.departmentId)
              this.form.get('companyInfoForm.designationId')?.setValue(employee.designationId)
            }
            // }
          }
        })
    }
    this.employeeMemberId = ''
    this.bankId = ''
    // if (options && options.businessId) {
    //   this.form.get('companyInfoForm.businessId')?.setValue(options.businessId)
    // }

    if (options && options.memberType === 'Self') {
      this.personalform.get('passport')?.setValue(options.passport)
      this.personalform.get('passportExpiryDate')?.setValue(options.passportExpiryDate)
      this.personalform.get('aadharNo')?.setValue(options.aadharNo)
      this.personalform.get('PAN')?.setValue(options.PAN)
      this.personalform.get('pfUAN')?.setValue(options.pfUAN ?? '')
      this.personalform.get('esiNumber')?.setValue(options.esiNumber ?? '')
      this.personalform.get('nationality')?.setValue(options.nationality)
      this.personalform.get('religion')?.setValue(options.religion)
      this.personalform.get('maritalStatus')?.setValue(options.maritalStatus)
      this.personalform.get('isSpouseEmployed')?.setValue(options.isSpouseEmployed)
      this.personalform.get('noOfChildren')?.setValue(options.noOfChildren)
    }
    if (options && options.memberType === 'Family') {
      this.employeeMemberId = options.id
      this.familyform.get('name')?.setValue(options.name)
      this.familyform.get('relation')?.setValue(options.relation)
      this.familyform.get('mobile')?.setValue(options.mobile)
      this.familyform.get('birthdate')?.setValue(options.birthdate)
    }
    if (options && options.bank) {
      this.bankId = options.id
      this.bankForm.get('bank')?.setValue(options.bank)
      this.bankForm.get('branch')?.setValue(options.branch)
      this.bankForm.get('ifsc')?.setValue(options.ifsc)
      this.bankForm.get('accountNo')?.setValue(options.accountNo)
    }
    // if (options && options.degree) {
    //   const formatToMonth = (date: string | Date) => {
    //     const d = new Date(date)
    //     const month = `${d.getMonth() + 1}`.padStart(2, '0')
    //     const year = d.getFullYear()
    //     return `${year}-${month}`
    //   }

    //   this.educationForm.get('startDate')?.setValue(formatToMonth(options.startDate))
    //   this.educationForm.get('endDate')?.setValue(formatToMonth(options.endDate))

    //   this.educationId = options.id
    //   this.educationForm.get('degree')?.setValue(options.degree)
    //   this.educationForm.get('university')?.setValue(options.university)
    //   // this.educationForm.get('startDate')?.setValue(options.startDate)
    //   // this.educationForm.get('endDate')?.setValue(options.endDate)
    // }
    if (options && options.degree) {
      const getYear = (date: string | Date) => new Date(date).getFullYear()

      this.educationForm.get('startDate')?.setValue(getYear(options.startDate))
      this.educationForm.get('endDate')?.setValue(getYear(options.endDate))

      this.educationId = options.id
      this.educationForm.get('degree')?.setValue(options.degree)
      this.educationForm.get('university')?.setValue(options.university)
    }

    if (options && options.company) {
      const formatToMonth = (date: string | Date) => {
        const d = new Date(date)
        const month = `${d.getMonth() + 1}`.padStart(2, '0')
        const year = d.getFullYear()
        return `${year}-${month}`
      }
      this.experienceId = options.id
      this.experienceForm.get('company')?.setValue(options.company)
      this.experienceForm.get('designation')?.setValue(options.designation)
      // this.experienceForm.get('startDate')?.setValue(formatToMonth(options.startDate))
      this.experienceForm.get('startDate')?.setValue(options.startDate)
      if (!options.isPresent) {
        // this.experienceForm.get('endDate')?.setValue(formatToMonth(options.endDate))
        this.experienceForm.get('endDate')?.setValue(options.endDate)
      }
      this.experienceForm.get('isPresent')?.setValue(options.isPresent)
    }
    // console.log('options', options)
    if (options && options.effectiveLastDay) {
      this.offBoardId = options.id
      this.offBoardForm.get('date')?.setValue(options.date)
      this.offBoardForm.get('effectiveLastDay')?.setValue(options.effectiveLastDay)
      this.offBoardForm.get('isSalaryHold')?.setValue(options.isSalaryHold)
      this.offBoardForm.get('salaryHoldFrom')?.setValue(options.salaryHoldFrom)
      this.offBoardForm.get('salaryHoldTo')?.setValue(options.salaryHoldTo)
      this.offBoardForm.get('offboardingType')?.setValue(options.offboardingType)
      this.offBoardForm.get('reason')?.setValue(options.reason)
      this.offBoardForm.get('fullNFinalDate')?.setValue(options.fullNFinalDate)
      this.offBoardForm.get('isFullNFinalRequired')?.setValue(options.isFullNFinalRequired)
    }
    if (options) {
      // this.emergencyContact = JSON.parse(this.employee.emergencyContact);

      this.emergencyContactform?.get('nameP')?.setValue(options.nameP)
      this.emergencyContactform?.get('relationshipP')?.setValue(options.relationshipP)
      this.emergencyContactform?.get('phoneP')?.setValue(options.phoneP)
      this.emergencyContactform?.get('phone2P')?.setValue(options.phone2P)
      this.emergencyContactform?.get('nameS')?.setValue(options.nameS)
      this.emergencyContactform?.get('relationshipS')?.setValue(options.relationshipS)
      this.emergencyContactform?.get('phoneS')?.setValue(options.phoneS)
      this.emergencyContactform?.get('phone2S')?.setValue(options.phone2S)
    }

    // Conditionally call API if assignManagerModal is opened
    if (options?.modalType === 'assignManager') {
      this.assignManagerform.reset({
        managerId: null,
        reqManagerId: null,
        hrManagerId: null,
        userId: this.employeeId
      })
      this.getAssignManager()
    }

    if (options && options.picture) {
      this.imageURL = `${this.apiEndpoint}/uploads/${options.picture}`
    } else if (!options?.picture && !options?.bank && !options?.degree && !options?.company) {
      this.imageURL = this.defaultImg
    }

    if (modalType === 'assignPermissionSetsModal') {
      // this.assignedPermissionSetsService
      //   .getAssignedPermissionSets(this.employeeId, null, 1, 100)
      //   .subscribe(res => {
      //     const assignedList = res.assignedPermissionSets || []
      //     this.assignedPermissionSets = assignedList // ✅ ab hamesha SAHI employee ka data

      //     const assignedIds = new Set(
      //       assignedList.map((a: any) => a.permissionSet?.id || a.permissionSetId)
      //     )

      //     this.store
      //       .select(selectRoles)
      //       .pipe(
      //         filter(roles => roles?.length > 0),
      //         take(1)
      //       )
      //       .subscribe(roles => {
      //         const allPermissionSetRoles = roles.filter(r => r.roleType === 'Permission Sets')
      //         this.permissionSetsList = allPermissionSetRoles.filter(r => !assignedIds.has(r.id))
      //         this.updateFormControl()
      //       })
      //   })

      // this.assignedPermissionSetsService
      //   .getAssignedPermissionSets(this.employeeId, 'disabled=true', 1, 100)
      //   .subscribe(res => {
      //     const list = res.assignedPermissionSets || []
      //     this.permissionSetsList = list.map((item: any) => {
      //       if (item.permissionSet) {
      //         return {
      //           id: item.permissionSet.id,
      //           name: item.permissionSet.name,
      //           ...item.permissionSet
      //         }
      //       }
      //       return item
      //     })
      //     this.updateFormControl()
      //   })

      // this.assignedPermissionSetsService
      //   .getAssignedPermissionSets(this.employeeId, 'disabled=false', 1, 100)
      //   .subscribe(res => {
      //     const list = res.assignedPermissionSets || []
      //     this.assignedPermissionSets = list.map((item: any) => {
      //       if (item.permissionSet) {
      //         return {
      //           id: item.permissionSet.id,
      //           name: item.permissionSet.name,
      //           ...item.permissionSet
      //         }
      //       }
      //       return item
      //     })
      //     this.updateFormControl()
      //   })

      this.assignedPermissionSetsService
        .getAllPermissionSets(this.user?.businessId, 1, 1000)
        .subscribe(res => {
          const list = res?.permissionSets || res?.data || res || []
          this.permissionSetsList = list.map((item: any) => ({
            id: item.id,
            name: item.name,
            ...item
          }))

          this.assignedPermissionSetsService
            .getAssignedPermissionSets(this.employeeId, 'disabled=false', 1, 1000)
            .subscribe(assignedRes => {
              const assignedList = assignedRes.assignedPermissionSets || []
              this.assignedPermissionSets = assignedList.map((item: any) => {
                if (item.permissionSet) {
                  return {
                    id: item.permissionSet.id,
                    name: item.permissionSet.name,
                    ...item.permissionSet
                  }
                }
                return item
              })

              // ⭐ Remove already-assigned items from the Available list
              const assignedIds = new Set(this.assignedPermissionSets.map((a: any) => a.id))
              this.permissionSetsList = this.permissionSetsList.filter(
                (p: any) => !assignedIds.has(p.id)
              )

              this.updateFormControl()
            })
        })
    }
  }

  onClose() {
    this.selectedSalaryId = null
    try {
      // if (this.form) {
      //   this.form.reset()
      // }
      // if (this.modalRef) {
      //   this.modalRef.close()
      // }
      if (this.originalFormValue) {
        this.form.reset(this.originalFormValue)
        this.form.markAsPristine()
        this.form.markAsUntouched()
        //Clear unsaved image and showing saved image for edit emoloyee
        const savedPicture = this.originalFormValue.basicInfoForm?.picture
        if (savedPicture && typeof savedPicture === 'string') {
          this.imageURL = `${this.apiEndpoint}/uploads/${savedPicture}`
        } else {
          this.imageURL = this.defaultImg // fallback to placeholder icon
        }

        //Clear country,state,city and showing saved value in edit
        const countryId = this.originalFormValue.addressInfoForm.country_id
        const stateId = this.originalFormValue.addressInfoForm.state_id
        this.selectedCountryId = countryId?.toString() || ''
        this.selectedStateId = stateId?.toString() || ''
        if (countryId) {
          this.onCountrySelect(this.selectedCountryId)
        }
        if (stateId) {
          this.onStateSelect(this.selectedStateId)
        }
      }

      //Reset
      this.experienceForm.reset()
      this.educationForm.reset()
      this.familyform.reset()
      this.bankForm.reset()
      this.changePasswordForm.reset()
      this.offBoardForm.reset({
        isSalaryHold: false,
        isFullNFinalRequired: false
      })
      this.store.dispatch(clearStates())
      this.store.dispatch(clearCities())
      this.emergencyContactform.reset()
      this.emergencyContactform.markAsPristine()
      this.emergencyContactform.markAsUntouched()
      this.modalService.dismissAll()
      this.assignManagerform.reset({
        managerId: null,
        reqManagerId: null,
        hrManagerId: null,
        userId: this.employeeId
      })
      this.getAssignManager()
      this.currentStep = 1
    } catch (error) {
      console.error('Error closing modal:', error)
    }
  }

  getErrorMessage(controlName: string, form?: FormGroup): string | null {
    // const control = this.form.get(controlName)
    const control = form?.get(controlName)

    // console.log('control name', control)
    if (control && control.touched && control.invalid) {
      if (control.hasError('required')) {
        return 'This field is required'
      }
      if (control.hasError('email')) {
        return 'Please enter a valid email address'
      }
      if (control.hasError('pattern')) {
        if (controlName === 'ifsc') {
          return 'Invalid IFSC (e.g. HDFC0001234)'
        }
        if (controlName === 'accountNo') {
          return 'Account number must be 9–18 digits'
        }
        if (controlName === 'branch') {
          return 'Only letters, numbers, space and dash allowed'
        }
        // Check if it's a URL pattern error for social media fields
        if (controlName.includes('facebook')) {
          return 'Please enter a valid Facebook URL'
        } else if (controlName.includes('twitter')) {
          return 'Please enter a valid Twitter URL'
        } else if (controlName.includes('linkedin')) {
          return 'Please enter a valid LinkedIn URL'
        } else if (controlName.includes('skype')) {
          return 'Please enter a valid Skype username or invite link'
        } else if (controlName.includes('whatsapp')) {
          return 'Please enter a valid WhatsApp link'
        } else if (controlName.includes('instagram')) {
          return 'Please enter a valid Instagram URL'
        } else if (controlName.includes('website')) {
          return 'Please enter a valid website URL'
        } else {
          return 'The entered value is not valid'
        }
      }
      if (control?.hasError('invalidType')) {
        return 'Please select a valid image file.'
      }
    }
    return null // No error
  }

  // employee-detail.component.ts
  onFamilySubmit() {
    if (this.personalform.invalid) {
      this.personalform.markAllAsTouched()
      return
    }

    if (!this.selfMember) {
      const payload = this.personalform.value
      payload.memberType = this.memberTypeEnum.SELF
      this.store.dispatch(createFamily({ payload }))
    } else if (this.selfMember) {
      const payload = this.personalform.value
      this.store.dispatch(updateFamily({ id: this.selfMember.id, payload }))
    }

    if (!this.selfMember) {
      const payload = {
        ...this.personalform.value,
        memberType: this.memberTypeEnum.SELF,
        userId: this.employeeId
      }
      this.store.dispatch(createFamily({ payload }))
    } else {
      const payload = { ...this.personalform.value }
      this.store.dispatch(updateFamily({ id: this.selfMember.id, payload }))
    }
  }

  // addFamilyMember() {
  //   if (this.familyform.invalid) {
  //     this.familyform.markAllAsTouched()
  //     return
  //   }

  //   if (!this.employeeMemberId) {
  //     const payload = this.familyform.value
  //     payload.memberType = this.memberTypeEnum.FAMILY
  //     payload.userId = this.employeeId
  //     this.store.dispatch(createFamily({ payload }))
  //   } else if (this.employeeMemberId) {
  //     const payload = this.familyform.value
  //     this.store.dispatch(updateFamily({ id: this.employeeMemberId, payload }))
  //     this.employeeMemberId = ''
  //   }
  // }

  addFamilyMember() {
    if (this.familyform.invalid) {
      this.familyform.markAllAsTouched()
      return
    }

    const formValue = this.familyform.value

    if (!this.employeeMemberId) {
      const payload = {
        ...formValue,
        memberType: this.memberTypeEnum.FAMILY,
        userId: this.employeeId
      }
      this.store.dispatch(createFamily({ payload }))
    } else {
      const payload = { ...formValue }
      this.store.dispatch(updateFamily({ id: this.employeeMemberId, payload }))
      this.employeeMemberId = ''
    }
  }

  openDeleteModal(recordId: string, type: string) {
    const modalRef = this.modalService.open(DeleteComponent)
    modalRef.componentInstance.recordId = recordId
    modalRef.result.then(
      result => {
        // Handle the result from the modal
        if (result) {
          // Call the service to update the business status
          this.handleDeleteConfirm(result, type)
        }
      },
      reason => {
        // Handle the dismiss case
        if (reason) {
          this.handleDeleteConfirm(reason, type)
        }
      }
    )
  }

  handleDeleteConfirm(data: { id: string }, type: string) {
    if (type === 'family') {
      this.store.dispatch(deleteFamily({ id: data.id }))
    }
    if (type === 'bank') {
      this.store.dispatch(deleteBank({ id: data.id }))
    }
    if (type === 'education') {
      this.store.dispatch(deleteEducation({ id: data.id }))
    }
    if (type === 'experience') {
      this.store.dispatch(deleteExperience({ id: data.id }))
    }
    if (type === 'offBoard') {
      this.empResignationService.delete(data.id).subscribe({
        next: res => {
          console.log('Deleted successfully')
          this.offBoardId = ''
          this.offBoardForm.reset()
          this.store.dispatch(loadEmployeeById({ id: this.employeeId }))
          this.loadOffboarding()
          setTimeout(() => {
            const accordion = document.getElementById('primaryBorderSix')

            if (accordion) {
              accordion.classList.remove('show')
            }

            const toggleBtn = document.querySelector(
              '[data-bs-target="#primaryBorderSix"]'
            ) as HTMLElement

            if (toggleBtn) {
              toggleBtn.classList.add('collapsed')
              toggleBtn.setAttribute('aria-expanded', 'false')
            }
          }, 100)
        },
        error: err => {
          console.error(err)
        }
      })
    }
  }

  addBank() {
    if (this.bankForm.invalid) {
      this.bankForm.markAllAsTouched()
      this.bankForm.updateValueAndValidity()
      return
    }
    this.bankloading = true
    const formValue = this.bankForm.value

    if (!this.bankId) {
      const payload = { ...formValue, userId: this.employeeId }
      this.store.dispatch(createBank({ payload }))
    } else {
      const payload = { ...formValue }
      this.store.dispatch(updateBank({ id: this.bankId, payload }))
      this.bankId = ''
    }
  }

  addEducation() {
    if (this.educationForm.invalid) {
      this.educationForm.markAllAsTouched()
      return
    }

    const formValue = this.educationForm.value

    // Append '-01' only if the date is in 'YYYY-MM' format (not already 'YYYY-MM-DD')
    // if (/^\d{4}-\d{2}$/.test(formValue.startDate)) {
    formValue.startDate = `${formValue.startDate}-01-01`
    // }
    // if (/^\d{4}-\d{2}$/.test(formValue.endDate)) {
    formValue.endDate = `${formValue.endDate}-01-01`
    // }

    if (!this.educationId) {
      const payload = { ...formValue, userId: this.employeeId }
      this.store.dispatch(createEducation({ payload }))
    } else {
      const payload = { ...formValue }
      this.store.dispatch(updateEducation({ id: this.educationId, payload }))
      this.educationId = ''
    }
  }

  // addEducation() {
  //   if (this.educationForm.invalid) {
  //     this.educationForm.markAllAsTouched()
  //     return
  //   }

  //   const formValue = { ...this.educationForm.value }

  //   // If startDate is year-only (YYYY), append '-01-01'
  //   if (/^\d{4}$/.test(formValue.startDate)) {
  //     formValue.startDate = `${formValue.startDate}-01-01`
  //   }
  //   // If startDate is year-month (YYYY-MM), append '-01'
  //   else if (/^\d{4}-\d{2}$/.test(formValue.startDate)) {
  //     formValue.startDate = `${formValue.startDate}-01`
  //   }

  //   // Same for endDate
  //   if (/^\d{4}$/.test(formValue.endDate)) {
  //     formValue.endDate = `${formValue.endDate}-01`
  //   } else if (/^\d{4}-\d{2}$/.test(formValue.endDate)) {
  //     formValue.endDate = `${formValue.endDate}-01`
  //   }

  //   if (!this.educationId) {
  //     const payload = { ...formValue, userId: this.employeeId }
  //     this.store.dispatch(createEducation({ payload }))
  //   } else {
  //     const payload = { ...formValue }
  //     this.store.dispatch(updateEducation({ id: this.educationId, payload }))
  //     this.educationId = ''
  //   }
  // }

  addExperience() {
    if (this.experienceForm.invalid) {
      this.experienceForm.markAllAsTouched()
      return
    }
    const formValue = this.experienceForm.value
    // Convert YYYY-MM -> YYYY-MM-01
    // formValue.startDate = `${formValue.startDate}-01`
    // // formValue.endDate = `${formValue.endDate}-01`
    // if (!formValue.isExperienced) {
    //   formValue.endDate = `${formValue.endDate}-01`
    // } else {
    //   delete formValue.endDate // Exclude endDate if currently working
    // }

    // Append '-01' only if the date is in 'YYYY-MM' format (not already 'YYYY-MM-DD')
    // if (/^\d{4}-\d{2}$/.test(formValue.startDate)) {
    //   formValue.startDate = `${formValue.startDate}-01`
    // }
    // if (/^\d{4}-\d{2}$/.test(formValue.endDate)) {
    //   formValue.endDate = `${formValue.endDate}-01`
    // } else {
    //   delete formValue.endDate // Exclude endDate if currently working
    // }
    if (formValue.isPresent === null || (formValue.isPresent === undefined && formValue.endDate)) {
      formValue.isPresent = false
    }

    if (!this.experienceId) {
      const payload = { ...formValue, userId: this.employeeId }
      this.store.dispatch(createExperience({ payload }))
    } else {
      const payload = { ...formValue }
      this.store.dispatch(updateExperience({ id: this.experienceId, payload }))
      this.educationId = ''
    }
  }

  // dateRangeValidator(startDateKey: string, endDateKey: string, isPresentKey: string): ValidatorFn {
  //   return (control: AbstractControl): { [key: string]: any } | null => {
  //     const startDate = control.get(startDateKey)?.value
  //     const endDate = control.get(endDateKey)?.value
  //     const isPresent = control.get(isPresentKey)?.value
  //     const today = new Date()
  //     const todayString = new Date(today.getFullYear(), today.getMonth(), today.getDate())

  //     // Skip validation if isPresent is checked
  //     if (isPresent) {
  //       // Allow endDate to be blank
  //       return null
  //     }

  //     // Perform validations only if isPresent is not checked
  //     // if (startDate && !isPresent) {
  //     //   this.experienceForm.get('isPresent')?.setValue(false)
  //     //   if (endDate) {
  //     //     // Check if endDate is greater than today
  //     //     if (new Date(endDate) > todayString) {
  //     //       return { endDateGreaterThanToday: true }
  //     //     }
  //     //     // Check if endDate is less than startDate
  //     //     if (new Date(endDate) < new Date(startDate)) {
  //     //       return { endDateLessThanStartDate: true }
  //     //     }
  //     //   } else {
  //     //     // If endDate is required but is blank when isPresent is not checked
  //     //     return { endDateRequired: true } // Optionally, add a specific error if endDate is required
  //     //   }
  //     // }

  //     return null // Return null if no validation errors
  //   }
  // }

  // dateRangeValidator(startDateKey: string, endDateKey: string, isPresentKey: string): ValidatorFn {
  //   return (control: AbstractControl): { [key: string]: any } | null => {
  //     const startDate = control.get(startDateKey)?.value
  //     const endDate = control.get(endDateKey)?.value
  //     const isPresent = control.get(isPresentKey)?.value
  //     const today = new Date()
  //     const todayString = new Date(today.getFullYear(), today.getMonth(), today.getDate())

  //     // Skip validation if isPresent is checked
  //     if (isPresent) {
  //       return null
  //     }

  //     if (startDate) {
  //       if (endDate) {
  //         const end = new Date(endDate)
  //         const start = new Date(startDate)
  //         // End date can't be greater than today
  //         if (end > todayString) {
  //           return { endDateGreaterThanToday: true }
  //         }
  //         // End date can't be less than start date
  //         if (end < start) {
  //           return { endDateLessThanStartDate: true }
  //         }
  //       } else {
  //         // End date is required if isPresent is not checked
  //         return { endDateRequired: true }
  //       }
  //     }

  //     return null
  //   }
  // }

  dateRangeValidator(startDateKey: string, endDateKey: string, isPresentKey: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const startValue = control.get(startDateKey)?.value
      const endValue = control.get(endDateKey)?.value
      const isPresent = control.get(isPresentKey)?.value
      const today = new Date()

      // Skip validation if isPresent is checked
      if (isPresent) {
        return null
      }

      if (!startValue) return null

      if (!endValue) return { endDateRequired: true }

      // Determine type: year-only or month-year
      const isYearOnly = /^\d{4}$/.test(startValue) && /^\d{4}$/.test(endValue)

      if (isYearOnly) {
        const startYear = Number(startValue)
        const endYear = Number(endValue)

        if (endYear > today.getFullYear()) return { endDateGreaterThanToday: true }
        if (endYear < startYear) return { endDateLessThanStartDate: true }
      } else {
        // Assume YYYY-MM format
        const startDate = new Date(startValue + '-01') // first day of month
        const endDate = new Date(endValue + '-01')

        // End date can't be greater than today
        if (endDate > today) return { endDateGreaterThanToday: true }
        // End date can't be before start date
        if (endDate < startDate) return { endDateLessThanStartDate: true }
      }

      return null
    }
  }

  mobileNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const mobileNumber = control.value
      // const valid = /^\+\d{1,14}$/.test(phoneNumber); // Adjust the regex as needed
      // const valid = /^\+\d{1,3}[\s()\-]?\d{1,14}([\s()\-]?\d{1,4})*$/.test(phoneNumber);
      // const valid = /^\+\d{1,3} \d{1,14}$/.test(phoneNumber);
      const valid = /^\+\d{1,3}[\s]?\d{1,14}$/.test(mobileNumber)

      return valid ? null : { invalidMobileNumber: true }
    }
  }

  updateEmergencyContact() {
    if (this.emergencyContactform.invalid) {
      this.emergencyContactform.markAllAsTouched()
      return
    }

    const emergencyContact = this.emergencyContactform.value
    const formData = new FormData()
    formData.append('emergencyContact', JSON.stringify(emergencyContact))

    this.store.dispatch(updateEmployee({ id: this.employeeId, data: formData }))
  }

  // Custom validator for ownerpersonalPhone
  conditionalMobileValidator() {
    return (control: AbstractControl) => {
      if (control.value) {
        // Validate using your phoneNumberValidator if value exists
        return this.mobileNumberValidator()(control)
      }
      return null // No error if value is empty
    }
  }

  // Custom validator for ownerpersonalEmail
  conditionalEmailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value ? control.value.trim() : ''
      if (value) {
        // Use regex email pattern (from Angular source) on the trimmed value:
        const EMAIL_REGEXP = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/i
        return EMAIL_REGEXP.test(value) ? null : { email: true }
        // Or, if you want to use Angular's validator:
        // return Validators.email({ ...control, value }); // But this is NOT allowed!
      }
      return null
    }
  }

  ageValidator(minAge: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value) {
        const birthdate = new Date(control.value)
        const today = new Date()
        const age = today.getFullYear() - birthdate.getFullYear()
        const monthDifference = today.getMonth() - birthdate.getMonth()

        // Adjust age if the birthdate has not occurred yet this year
        if (
          monthDifference < 0 ||
          (monthDifference === 0 && today.getDate() < birthdate.getDate())
        ) {
          return age < minAge ? { ageInvalid: true } : null
        }

        return age < minAge ? { ageInvalid: true } : null
      } else {
        return null
      }
    }
  }

  isStepValid(): boolean {
    switch (this.currentStep) {
      case 1:
        return !!this.form.get('basicInfoForm')?.valid
      case 2:
        return !!this.form.get('companyInfoForm')?.valid
      // &&
      //        !!this.form.get('email')?.valid &&
      //        !!this.form.get('phoneNumber1')?.valid &&
      //        !!this.form.get('phoneNumber2')?.valid;
      case 3:
        return !!this.form.get('addressInfoForm')?.valid
      // &&
      //        !!this.form.get('city')?.valid &&
      //        !!this.form.get('state')?.valid &&
      //        !!this.form.get('country')?.valid &&
      //        !!this.form.get('zipcode')?.valid;
      case 4:
        return !!this.form.get('socialInfoForm')?.valid
      default:
        return false
    }
  }

  // Function to go to the next step
  goToNextStep(): void {
    // this.store.dispatch(updateEmployee({ id: this.employeeId, data:this.user?.firstName }))
    if (this.isStepValid()) {
      if (this.currentStep === 1) {
        this.updateUser()
      }
      if (this.currentStep === 2) {
        this.updateUser()
      }
      if (this.currentStep === 3) {
        this.updateEmployeeAddress()
      }
      if (this.currentStep === 4) {
        this.updateUser()
        this.onClose()
        this.currentStep = 0
      }
      // if (this.currentStep == 5) {
      //   this.updateEmployeePermission()
      // }
      this.currentStep++
    } else {
      alert('Please complete the form before proceeding!')
    }
  }

  stepChange(newStep: number): void {
    // if (this.isStepValid()) {
    this.currentStep = newStep
    // }
  }

  // Submit the form
  onSubmit(): void {
    if (this.form.valid) {
      this.onClose()
    } else {
      alert('Please fill all required fields!')
    }
  }

  onFileChange(event: Event, imageid: string) {
    const inputElement = event.target as HTMLInputElement
    if (inputElement.files && inputElement.files.length > 0) {
      const file = inputElement.files[0]
      const validationResult = validateImage(file)

      if (validationResult.valid) {
        // console.log('Image is valid!', file)
        this.form.get('basicInfoForm.picture')?.setValue('')
        this.form.get('basicInfoForm.picture')?.setValue(file) // Set the file in the form control
        // this.validationError = '' // Clear any previous error messages
      } else {
        console.error('Validation error:', validationResult.message)
        // this.validationError = validationResult.message // Set the error message
      }

      const reader = new FileReader()
      reader.onload = e => {
        if (imageid === 'imageURL') {
          this.imageURL = e.target?.result as string
        }
      }

      reader.readAsDataURL(file)
    }
  }

  updateUser() {
    const formValue = this.form.value
    const formData = new FormData()

    const flattenAndAppend = (obj: any, excludeProfilePicture: boolean = false) => {
      Object.entries(obj).forEach(([key, value]) => {
        // Skip departmentId specifically from companyInfoForm
        if (key === 'departmentId' && obj === formValue.companyInfoForm) {
          return
        }
        if (excludeProfilePicture && key === 'picture') {
          return // Exclude profilePicture from basicInfoForm
        }
        if (Array.isArray(value)) {
          value.forEach((val, i) => {
            const safeValue = typeof val === 'object' ? JSON.stringify(val) : String(val)
            formData.append(`${key}[${i}]`, safeValue)
          })
        } else if (value !== null && value !== undefined) {
          const safeValue = typeof value === 'object' ? JSON.stringify(value) : String(value)
          formData.append(key, safeValue)
        }
      })
    }
    flattenAndAppend(formValue.basicInfoForm)
    flattenAndAppend(formValue.companyInfoForm)
    // flattenAndAppend(formValue.addressInfoForm)
    flattenAndAppend(formValue.socialInfoForm)
    // flattenAndAppend(formValue.rolepermissionInfoForm)

    // Add the profile picture if available
    const profilePicture = this.form.get('basicInfoForm.picture')?.value

    if (profilePicture instanceof File) {
      // console.log(profilePicture)
      formData.append('picture', profilePicture, profilePicture.name) // Add the picture file to formData
    }

    this.store.dispatch(updateEmployee({ id: this.employeeId, data: formData }))
  }

  onCountrySelect(countryId: string): void {
    this.selectedCountryId = countryId
    if (this.form.get('addressInfoForm.country_id')?.dirty) {
      this.form.get('addressInfoForm.state_id')?.setValue('')
      this.form.get('addressInfoForm.cityId')?.setValue('')
    }
    this.store.dispatch(loadStates({ countryId }))
  }

  onStateSelect(stateId: string): void {
    this.selectedStateId = stateId
    if (this.form.get('addressInfoForm.state_id')?.dirty) {
      this.form.get('addressInfoForm.cityId')?.setValue('')
    }
    this.store.dispatch(loadCities({ stateId }))
  }

  onBusinessSelect(businessId: string) {
    this.store.dispatch(resetDepartmentState())
    this.store.dispatch(resetDesignationState())
    if (businessId) {
      // Call a method to fetch data based on the selected owner
      const params = `businessId=${encodeURIComponent(businessId)}&disabled=false`
      this.store.dispatch(loadOffices({ params })) // Dispatch action with filter
      this.store.dispatch(loadDepartments({ params })) // Dispatch action with filter
    }
  }

  onDepartmentSelect(departmentId: string) {
    if (departmentId) {
      const params = `departmentId=${encodeURIComponent(departmentId)}&disabled=false`
      this.store.dispatch(loadDesignations({ params })) // Dispatch action with filter
    }
  }

  updateEmployeeAddress() {
    const formValue = this.form.value
    const payload = { ...formValue.addressInfoForm }

    // Remove country_id and state_id
    delete payload.country_id
    delete payload.state_id

    payload.memberType = this.memberTypeEnum.SELF
    payload.userId = this.employeeId

    if (!this.selfMember) {
      this.store.dispatch(createFamily({ payload }))
    } else {
      this.store.dispatch(updateFamily({ id: this.selfMember.id, payload }))
    }
  }

  // updateEmployeeAddress() {
  //   const formValue = this.form.value
  //   const addressForm = formValue.addressInfoForm

  //   combineLatest([this.cities$ ?? of([]), this.states$ ?? of([]), this.countries$ ?? of([])])
  //     .pipe(take(1))
  //     .subscribe(([cities, states, countries]) => {
  //       const selectedCity = (cities as any[]).find(
  //         c => String(c.id) === String(addressForm.cityId)
  //       )
  //       const selectedState = (states as any[]).find(
  //         s => String(s.id) === String(addressForm.state_id)
  //       )
  //       const selectedCountry = (countries as any[]).find(
  //         c => String(c.id) === String(addressForm.country_id)
  //       )

  //       const payload: any = {
  //         address: addressForm.address,
  //         cityId: addressForm.cityId,
  //         zipCode: addressForm.zipCode,
  //         memberType: this.memberTypeEnum.SELF,
  //         userId: this.employeeId,
  //         cityName: selectedCity?.name || null,
  //         stateName: selectedState?.name || null,
  //         countryName: selectedCountry?.name || null
  //       }

  //       if (!this.selfMember) {
  //         this.store.dispatch(createFamily({ payload }))
  //       } else {
  //         this.store.dispatch(updateFamily({ id: this.selfMember.id, payload }))
  //       }
  //     })
  // }

  prepareAttendancePolicyDrawer(policy: any, type: string) {
    this.selectedAttendancePolicy = policy
    this.selectedAttendancePolicy.policyType = type
  }

  prepareLeavePolicyDrawer(policy: any, type: string) {
    this.selectedLeavePolicy = policy
    this.selectedLeavePolicy.policyType = type
  }

  assignPermissionSet() {
    if (this.employeeId) {
      const formData = this.assignPermissionSetsForm.value
      // console.log(this.assignedPermissionSets)
      this.store.dispatch(
        createAssignedPermissionSets({
          permissionSetIds: formData.permissionSetIds,
          userId: this.employeeId
        })
      )
    }
  }

  get assignedPermissionSetIds(): FormArray {
    return this.assignPermissionSetsForm.get('permissionSetIds') as FormArray
  }

  onPermissionSetSelect(event: Event): void {
    const selectedOptions = (event.target as HTMLSelectElement).selectedOptions

    // Clear existing values
    this.assignedPermissionSetIds.clear()

    Array.from(selectedOptions).forEach(option => {
      const rawValue = option.value
      const idPart = rawValue.split(':')[1]?.trim()
      const permissionSetId = Number(idPart)

      if (!isNaN(permissionSetId)) {
        this.assignedPermissionSetIds.push(this.fb.control(permissionSetId, Validators.required))
      }
    })
  }

  /** Move selected items to Assigned Permission Sets */
  moveToAssigned() {
    if (this.selectedAvailable.length === 0) return
    // Move only selected items (filter out null, undefined, and empty objects)
    const selectedItems = this.permissionSetsList.filter(
      (item: { id?: any }) =>
        this.selectedAvailable.includes(item.id) &&
        item?.id !== null &&
        item?.id !== undefined &&
        Object.keys(item).length > 0 // Ensure object is not empty
    )

    if (selectedItems.length === 0) return // Ensure we don't push an empty array

    // Ensure no null/undefined values in assignedPermissionSets
    this.assignedPermissionSets = [
      ...this.assignedPermissionSets.filter(
        (item: { id: null | undefined }) => item?.id !== null && item?.id !== undefined
      ), // Remove invalid items
      ...selectedItems
    ]

    // console.log("Updated assignedPermissionSets:", this.assignedPermissionSets); // Debugging output

    // Remove moved items from the available list
    this.permissionSetsList = this.permissionSetsList.filter(
      (item: Role) => !this.selectedAvailable.includes(item.id)
    )
    // Update form control with assigned IDs
    this.updateFormControl()

    // Reset selection
    this.selectedAvailable = []
  }

  // moveToAvailable() {
  //   if (this.selectedAssigned.length === 0) return

  //   // Move only selected items (filter out null/undefined and empty objects)
  //   const selectedItems = this.assignedPermissionSets.filter(
  //     (item: { id?: any }) =>
  //       this.selectedAssigned.includes(item.id) &&
  //       item.id !== null &&
  //       item.id !== undefined &&
  //       Object.keys(item).length > 0 // Ensure the object is not empty
  //   )

  //   // console.log('available selected items', selectedItems)

  //   if (selectedItems.length === 0) return // Prevent pushing empty values

  //   // Ensure permissionSetsList does not contain empty items
  //   this.permissionSetsList = [
  //     ...this.permissionSetsList.filter((item: {}) => item && Object.keys(item).length > 0),
  //     ...selectedItems
  //   ]
  //   // Remove moved items from the assigned list
  //   this.assignedPermissionSets = this.assignedPermissionSets.filter(
  //     (item: { id: string }) =>
  //       !this.selectedAssigned.includes(item.id) && item.id !== null && item.id !== undefined
  //   )

  //   // Update form control with assigned IDs
  //   this.updateFormControl()

  //   // Reset selection
  //   this.selectedAssigned = []
  // }

  moveToAvailable() {
    if (this.selectedAssigned.length === 0) return

    const selectedItems = this.assignedPermissionSets.filter((item: any) => {
      const itemId = item.permissionSet?.id || item.id
      return this.selectedAssigned.includes(itemId) && itemId !== null && itemId !== undefined
    })

    if (selectedItems.length === 0) return

    const normalizedItems = selectedItems.map((item: any) => {
      if (item.permissionSet) {
        return { id: item.permissionSet.id, name: item.permissionSet.name, ...item.permissionSet }
      }
      return item
    })

    this.permissionSetsList = [
      ...this.permissionSetsList.filter((item: any) => item && Object.keys(item).length > 0),
      ...normalizedItems
    ]

    this.assignedPermissionSets = this.assignedPermissionSets.filter((item: any) => {
      const itemId = item.permissionSet?.id || item.id
      return !this.selectedAssigned.includes(itemId) && itemId !== null && itemId !== undefined
    })

    this.updateFormControl()
    this.selectedAssigned = []
  }

  // updateFormControl() {
  //   // Remove null or undefined IDs before updating the form
  //   const assignedIds = this.assignedPermissionSets
  //     .map((item: { id: any }) => item.id)
  //     .filter((id: null | undefined) => id !== null && id !== undefined)

  //   this.assignPermissionSetsForm.get('permissionSetIds')?.setValue(assignedIds)
  // }

  updateFormControl() {
    const assignedIds = this.assignedPermissionSets
      .map((item: any) => item.permissionSet?.id || item.id)
      .filter((id: any) => id !== null && id !== undefined)

    this.assignPermissionSetsForm.get('permissionSetIds')?.setValue(assignedIds)
  }

  /** Handle selection change for Available Permission Sets */
  onAvailableSelect(event: Event) {
    const options = (event.target as HTMLSelectElement).options
    this.selectedAvailable = Array.from(options)
      .filter(option => option.selected)
      .map(option => option.value)
  }

  /** Handle selection change for Assigned Permission Sets */
  onAssignedSelect(event: Event) {
    const options = (event.target as HTMLSelectElement).options
    this.selectedAssigned = Array.from(options)
      .filter(option => option.selected)
      .map(option => option.value)
  }

  ngOnDestroy(): void {
    this.destroyed$.next()
    this.destroyed$.complete()
  }

  loadAllBusinesses() {
    const params: any = {}
    if (this.businessIdss?.length) {
      params.businessIds = this.businessIdss.join(',')
    }

    this.store.dispatch(
      loadBusinesses({
        params: new URLSearchParams(params).toString()
      })
    )
  }

  openOffcanvasRoles(role: any) {
    this.resetPermissions()
    this.viewAssignedRole = role
    this.setPermissionsState(role.permissions)
    // this.permissionModules.forEach(module => {
    //   const viewPermission = `view_${module.name}`.toLowerCase()
    //   const addPermission = `add_${module.name}`.toLowerCase()
    //   const updatePermission = `update_${module.name}`.toLowerCase()
    //   const deletePermission = `delete_${module.name}`.toLowerCase()

    //   this.permissionsState[module.name] = {
    //     view: role.permissions.includes(viewPermission),
    //     add: role.permissions.includes(addPermission),
    //     edit: role.permissions.includes(updatePermission),
    //     delete: role.permissions.includes(deletePermission)
    //   }
    // })
  }

  setPermissionsState(permissions: Record<string, string[]>) {
    Object.entries(permissions).forEach(([module, actions]) => {
      this.permissionsState[module] = {
        view: actions.includes('view'),
        add: actions.includes('add'),
        edit: actions.includes('edit'),
        delete: actions.includes('delete'),
        status: actions.includes('status')
      }
    })
  }

  resetPermissions() {
    this.permissionsState = {}

    // Optional: reset all permissions to false
    this.permissionModules.forEach(module => {
      this.permissionsState[module.key] = {}
      module.permissions.forEach(action => {
        this.permissionsState[module.key][action] = false
      })
    })
  }

  goBack() {
    this.location.back()
  }

  openModal(isBlocking: boolean, employeeId: string, type: string) {
    const modalRef = this.modalService.open(BlockUnblockComponent)

    if (type === 'portal' && isBlocking === true) {
      modalRef.componentInstance.title = 'Give Portal Access'
      modalRef.componentInstance.desc = `Are you sure to give the portal access to the employee`
    }
    if (type === 'portal' && isBlocking === false) {
      modalRef.componentInstance.title = 'Revoke Portal Access'
      modalRef.componentInstance.desc = `Are you sure to remove portal access to the employee`
    }
    modalRef.componentInstance.isBlocking = isBlocking
    modalRef.componentInstance.recordId = employeeId
    // this.blockBusinessId = businessId

    modalRef.result.then(
      result => {
        // Handle the result from the modal
        if (result) {
          // Call the service to update the business status
          this.handleConfirm(result)
        }
      },
      reason => {
        // Handle the dismiss case
        if (reason) {
          this.handleConfirm(reason)
        }
      }
    )
  }

  // handleConfirm(data: { id: string; status: boolean; title: string }) {
  //   if (data.title === 'Revoke Portal Access' || data.title === 'Give Portal Access') {
  //     this.store.dispatch(portalAccessEmployee({ id: data.id, portalAccess: data.status }))
  //     // setTimeout(() => {
  //     this.store.dispatch(loadEmployeeById({ id: this.employeeId }))
  //     // }, 300)
  //   } else {
  //     this.store.dispatch(blockEmployee({ id: data.id, disabled: data.status }))
  //     // setTimeout(() => {
  //     this.store.dispatch(loadEmployeeById({ id: this.employeeId }))
  //     // }, 300)
  //   }
  // }

  handleConfirm(data: { id: string; status: boolean; title: string }) {
    if (data.title === 'Revoke Portal Access' || data.title === 'Give Portal Access') {
      this.store.dispatch(portalAccessEmployee({ id: data.id, portalAccess: data.status }))
    } else {
      this.store.dispatch(blockEmployee({ id: data.id, disabled: data.status }))
    }
  }

  isExpired(validUpto?: string | Date | null): boolean {
    if (!validUpto) return false
    const today = new Date()
    const expiry = new Date(validUpto)
    return expiry < today
  }

  onImageError(employee: any): void {
    employee.picture = null // fallback triggers initials
  }

  getInitials(firstName: string = '', lastName: string = ''): string {
    const firstInitial = firstName?.charAt(0) || ''
    const lastInitial = lastName?.charAt(0) || ''
    return (firstInitial + lastInitial).toUpperCase()
  }

  getFullAddress(member: any): string {
    if (!member) return ''

    let address = member.address ?? ''
    if (member.city) address += ', ' + member.city.name
    if (member.city?.state) address += ', <br>'
    if (member.city?.state) address += member.city.state.name
    if (member.city?.state?.country) address += ', '
    if (member.city?.state?.country) address += member.city.state.country.name
    if (member.zipCode) address += ' - ' + member.zipCode
    return address
  }

  loadTasklogs() {
    this.store.dispatch(
      activityLogActions.loadMyLogs({
        userId: this.employeeId,
        page: this.currentTasklogPage,
        limit: this.pageLimit || 10
      })
    )
  }

  // onPageChange(newPage: number): void {
  //   if (newPage > 0) {
  //     this.currentPage = newPage
  //     this.loadEmployees() // Reload businesses for the new page
  //     this.updatePageLastRecord()
  //   }
  // }
  // private updatePageLastRecord() {
  //   this.pageLastRecord = Math.min(this.currentPage * this.pageLimit, this.totalCount)
  // }

  onTasklogPageChange(newPage: number): void {
    if (newPage > 0) {
      this.currentTasklogPage = newPage
      this.loadTasklogs() // Reload businesses for the new page
      this.updateTasklogPageLastRecord()
    }
  }

  // private updateTasklogPageLastRecord() {
  //   console.log('currentTasklogPage:', this.currentTasklogPage)
  //   console.log('pageLimit:', this.pageLimit)
  //   console.log('totalTasklogCount:', this.totalTasklogCount)

  //   if (
  //     typeof this.currentTasklogPage === 'number' &&
  //     typeof this.pageLimit === 'number' &&
  //     typeof this.totalTasklogCount === 'number'
  //   ) {
  //     this.pageTasklogLastRecord = Math.min(
  //       this.currentTasklogPage * this.pageLimit,
  //       this.totalTasklogCount
  //     )
  //   } else {
  //     this.pageTasklogLastRecord = 0
  //     console.warn('One or more values are not numbers.')
  //   }

  //   console.log('pageTasklogLastRecord:', this.pageTasklogLastRecord)
  // }

  private updateTasklogPageLastRecord() {
    // console.log('current page type', typeof this.currentTasklogPage)
    // console.log('current page limit', typeof this.pageLimit)
    if (typeof this.currentTasklogPage === 'number' && typeof this.pageLimit === 'number') {
      const lastRecord = this.currentTasklogPage * this.pageLimit
      this.pageTasklogLastRecord = Math.min(lastRecord, this.totalTasklogCount)
    } else {
      this.pageTasklogLastRecord = 0
    }
  }

  // Format the Aadhar No. with spaces after 4th, 8th, and 12th digits
  formatAadharNo(event: any) {
    let value = event.target.value.replace(/\D/g, '') // Remove non-digit characters

    // Insert space after the 4th, 8th, and 12th digits
    if (value.length > 4 && value.length <= 8) {
      value = value.slice(0, 4) + ' ' + value.slice(4)
    } else if (value.length > 8 && value.length <= 12) {
      value = value.slice(0, 4) + ' ' + value.slice(4, 8) + ' ' + value.slice(8)
    } else if (value.length > 12) {
      value =
        value.slice(0, 4) +
        ' ' +
        value.slice(4, 8) +
        ' ' +
        value.slice(8, 12) +
        ' ' +
        value.slice(12, 19)
    }

    // Update the input value with formatted Aadhar No.
    event.target.value = value
  }

  sendPasswordResetEmail(): void {
    // Implement the logic to call your API for password reset
    if (this.employeeData) {
      this.store.dispatch(forgotPassword({ email: this.employeeData.email }))
    }
  }

  customSearchFn = (term: string, item: any) => {
    // const fullName = item && item.businessName ? item.businessName.toLowerCase() : ''
    // return fullName.includes(term ? term.toLowerCase() : '')
    if (!term) {
      return true
    }
    const searchTerm = term.toLowerCase().trim()
    const firstName = item.firstName ? item.firstName.toLowerCase() : ''
    const lastName = item.lastName ? item.lastName.toLowerCase() : ''
    const fullName = `${firstName} ${lastName}`
    return (
      firstName.includes(searchTerm) ||
      lastName.includes(searchTerm) ||
      fullName.includes(searchTerm)
    )
  }

  customRoleSearchFn = (term: string, item: any) => {
    const name = item && item.name ? item.name.toLowerCase() : ''
    return name.includes(term ? term.toLowerCase() : '')
  }

  assignManager() {
    if (this.assignManagerform.valid) {
      const payload = this.assignManagerform.value

      this.userService.assignManager(payload).subscribe({
        next: res => {
          // console.log('Manager assigned successfully:', res)
          this.getAssignManager()
          this.modalService.dismissAll() // or modalRef.close() if you're using one

          this.toasterNotification.success('Manager assigned successfully')
          // Optionally show a success message or reset the form
        },
        error: err => {
          this.toasterNotification.error(
            err?.error?.message || err.message || 'Something went wrong while assigning manager.'
          )
          // console.error('Error assigning manager:', err)
          // Optionally show an error message
        }
      })
    }
  }

  assignRole() {
    if (this.assignRoleform?.valid) {
      const roleId = this.assignRoleform.get('roleId')?.value
      if (roleId && this.employeeId) {
        this.store.dispatch(
          createAssignedTo({
            userRoleId: roleId,
            userData: [{ validUpto: null, userId: this.employeeId }]
          })
        )
      }
    }
  }

  setAsDefault(bank: any) {
    this.bankDetailService.setDefaultPolicy(bank.id).subscribe({
      next: res => {
        this.toasterNotification.success(res.message) // ✅ Show backend-provided success message
        const params = this.employeeId ? `userId=${encodeURIComponent(this.employeeId)}` : null
        // this.store.dispatch(loadBanks({ params }))
        setTimeout(() => {
          this.store.dispatch(loadBanks({ params }))
        }, 200)
      },
      error: err => {
        console.error('Failed to set default bank detail', err)
        this.toasterNotification.error(
          err?.error?.message || 'Something went wrong while updating the bank detail.'
        ) // ❌ Show meaningful error toast
      }
    })
  }

  // getAssignManager() {
  //   combineLatest([
  //     this.users$, // Wait until users are available
  //     this.userService.getAssignManager(this.employeeId)
  //   ]).subscribe({
  //     next: ([users, res]) => {
  //       if (res?.length > 0) {
  //         const data = res[res.length - 1]

  //         // console.log('Users:', users)
  //         // console.log('Assigned manager:', res[res.length - 1].manager.id)
  //         // console.log('Assigned reqManager:', res[res.length - 1].reqManager.id)

  //         // this.assignManagerform.patchValue({
  //         //   managerId: res[res.length - 1].manager.id,
  //         //   reqManagerId: res[res.length - 1].reqManager.id,
  //         //   userId: this.employeeId
  //         // })

  //         // this.assignManagerform.patchValue({
  //         //   managerId: data.manager.id,
  //         //   reqManagerId: data.reqManager.id,
  //         //   userId: this.employeeId
  //         // })

  //         if (data.manager?.id) {
  //           this.assignManagerform.get('managerId')?.setValue(data.manager.id)
  //           this.manager = data.manager.firstName + ' ' + data.manager.lastName
  //         }

  //         if (data.reqManager?.id) {
  //           this.assignManagerform.get('reqManagerId')?.setValue(data.reqManager.id)
  //           this.requestManager = data.reqManager.firstName + ' ' + data.reqManager.lastName
  //         }
  //       }
  //     },
  //     error: err => {
  //       this.toasterNotification.error(
  //         err?.error?.message ||
  //           err.message ||
  //           'Something went wrong while getting the assigned manager.'
  //       )
  //     }
  //   })
  // }

  getAssignManager() {
    // Guard: combineLatest throws "You provided 'undefined' where a stream was expected"
    // if either this.users$ or the service call isn't a valid Observable yet.
    if (!this.users$) {
      console.warn('getAssignManager() called before this.users$ was initialized — skipping')
      return
    }

    if (!this.employeeId) {
      console.warn('getAssignManager() called before this.employeeId was set — skipping')
      return
    }

    const assignManager$ = this.userService.getAssignManager(this.employeeId)

    if (!assignManager$) {
      console.error(
        'userService.getAssignManager() returned undefined/null instead of an Observable for id:',
        this.employeeId
      )
      return
    }

    combineLatest([
      this.users$, // Wait until users are available
      assignManager$
    ]).subscribe({
      next: ([users, res]) => {
        if (res?.length > 0) {
          const data = res[res.length - 1]

          if (data.manager?.id) {
            this.assignManagerform.get('managerId')?.setValue(data.manager.id)
            this.manager = data.manager.firstName + ' ' + data.manager.lastName
          } else {
            this.assignManagerform.get('managerId')?.setValue(null)
            this.manager = ''
          }

          if (data.reqManager?.id) {
            this.assignManagerform.get('reqManagerId')?.setValue(data.reqManager.id)
            this.requestManager = data.reqManager.firstName + ' ' + data.reqManager.lastName
          } else {
            this.assignManagerform.get('reqManagerId')?.setValue(null)
            this.requestManager = ''
          }

          if (data.hrManager?.id) {
            this.assignManagerform.get('hrManagerId')?.setValue(data.hrManager.id)
            this.hrManager = data.hrManager.firstName + ' ' + data.hrManager.lastName
          } else {
            this.assignManagerform.get('hrManagerId')?.setValue(null)
            this.hrManager = ''
          }
        }
      },
      error: err => {
        this.toasterNotification.error(
          err?.error?.message ||
            err.message ||
            'Something went wrong while getting the assigned manager.'
        )
      }
    })
  }

  loadUsers() {
    const params: any = {}
    // if (this.businessIdss?.length) {
    //   params.businessIds = this.businessIdss.join(',')
    // }
    params.businessId = this.employeeBusinessId
    params.disabled = false
    // if (this.businessId) {
    //   params.businessId = this.businessId
    // }

    this.store.dispatch(
      loadEmployees({
        params: new URLSearchParams(params).toString()
        // page: this.currentPage,
        // limit: this.pageLimit
      })
    )
  }

  onYearChange(event: Event) {
    const selected = (event.target as HTMLSelectElement).value
    // console.log('Selected year:', selected)
  }

  onChangePassword() {
    if (this.changePasswordForm.invalid) return

    const { oldPassword, newPassword } = this.changePasswordForm.value
    const userId = this.employeeId // Set this from your user list

    // Only send oldPassword and newPassword
    const payload = { oldPassword, newPassword }

    this.userService.changePassword(userId, payload).subscribe({
      next: () => {
        // alert('Password changed successfully.')
        this.toasterNotification.success('SUCCESS', 'Password changed successfully')

        this.changePasswordForm.reset()
        this.onClose()
      },
      error: err => {
        this.toasterNotification.error('ERROR', err.error.message || 'Failed to change password.')

        // alert(err.error?.message || 'Failed to change password.')
      }
    })
  }

  passwordsMatchValidator(form: AbstractControl) {
    const newPassword = form.get('newPassword')?.value
    const confirmPassword = form.get('confirmPassword')?.value
    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      return { passwordMismatch: true }
    }
    return null
  }

  get isAdmin(): boolean {
    return this.user?.userRoles?.some((ur: any) => ur.role?.name === 'Admin') ?? false
  }

  isContractor(employee: any): boolean {
    return employee?.userRoles?.some((r: UserRole) => r?.role?.name === 'Contractor') || false
  }

  get isBusinessOwner(): boolean {
    return this.user?.userRoles?.some((ur: any) => ur.role?.name === 'Business Owner') ?? false
  }

  get isViewedEmployeeBusinessOwner(): boolean {
    return (
      this.employeeData?.employmentType === 'Business Owner' ||
      this.employeeData?.userRoles?.some((ur: any) => ur?.role?.name === 'Business Owner')
    )
  }

  onOffBoard() {
    if (this.offBoardForm.invalid) return
    this.isSubmitting = true
    const {
      date,
      reason,
      effectiveLastDay,
      isSalaryHold,
      salaryHoldFrom,
      salaryHoldTo,
      offboardingType,
      isFullNFinalRequired,
      fullNFinalDate
    } = this.offBoardForm.value
    const userId = this.employeeId // Set this from your user list

    // Only send oldPassword and newPassword
    const payload = {
      date,
      reason,
      effectiveLastDay,
      userId,
      isSalaryHold,
      salaryHoldFrom,
      salaryHoldTo,
      offboardingType,
      isFullNFinalRequired,
      fullNFinalDate
    }
    if (!this.offBoardId) {
      this.empResignationService.create(payload).subscribe({
        // next: () => {
        //   // alert('Password changed successfully.')
        //   this.toasterNotification.success('SUCCESS', 'Off board successfully')
        //   if (this.offBoardForm) {
        //     this.offBoardForm.reset()
        //   }
        //   this.onClose()
        // },
        next: () => {
          this.isSubmitting = false
          this.toasterNotification.success('SUCCESS', 'Off board successfully')

          this.loadOffboarding()

          if (this.offBoardForm) {
            this.offBoardForm.reset()
          }

          this.onClose()

          setTimeout(() => {
            const accordion = document.getElementById('primaryBorderSix')

            if (accordion) {
              accordion.classList.add('show')
            }
          }, 300)
        },
        // error: err => {
        //   // console.log('Full error:', err)
        //   // // Try logging individual properties
        //   // console.log('Error status:', err.status)
        //   // console.log('Error text:', err.statusText)
        //   // console.log('Error body:', err.error)
        //   // // You can also check for message directly
        //   // console.log('Error message:', err.message)
        // }
        // error: (error: { message: string }) => {
        //   this.isSubmitting = false
        //   this.toasterNotification.error('Error: ' + error.message)
        // }
        error: (error: any) => {
          this.isSubmitting = false
          this.toasterNotification.error(
            error?.error?.message || error?.message || 'Something went wrong.'
          )
        }
      })
    } else {
      this.empResignationService.update(this.offBoardId, payload).subscribe({
        // next: () => {
        //   // alert('Password changed successfully.')
        //   this.toasterNotification.success('SUCCESS', 'Off board updated successfully')

        //   if (this.offBoardForm) {
        //     this.offBoardId = ''
        //     this.offBoardForm.reset()
        //   }
        //   this.onClose()
        // },
        next: () => {
          this.toasterNotification.success('SUCCESS', 'Off board updated successfully')

          this.loadOffboarding()

          if (this.offBoardForm) {
            this.offBoardId = ''
            this.offBoardForm.reset()
          }

          this.onClose()

          setTimeout(() => {
            const accordion = document.getElementById('primaryBorderSix')

            if (accordion) {
              accordion.classList.add('show')
            }
          }, 300)
        },
        // error: err => {
        //   // console.log('Full error:', err)
        //   // // Try logging individual properties
        //   // console.log('Error status:', err.status)
        //   // console.log('Error text:', err.statusText)
        //   // console.log('Error body:', err.error)
        //   // // You can also check for message directly
        //   // console.log('Error message:', err.message)
        // }
        error: (error: { message: string }) => {
          this.toasterNotification.error('Error: ' + error.message)
        }
      })
    }
  }

  loadOffboarding() {
    //let skip = this.currentPage * this.pageLimit - this.pageLimit
    //const search = this.search
    //  ? {
    //      $or: { title: { $like: `%${this.search}%` } },
    //      $and: { businessId: { $like: `%${this.selectedBusinessSort}%` } }
    //    }
    //  : {}

    this.empResignationService.list(this.employeeId).subscribe({
      next: response => {
        this.offboardingList = response.empResignations
        this.store.dispatch(loadEmployeeById({ id: this.employeeId }))
      },
      error: (error: { message: string }) => {
        this.toasterNotification.error('Error: ' + error.message)
      }
    })
  }

  // Format the Aadhar No. with spaces after 4th, 8th, and 12th digits
  // formatEsiNo(event: any) {
  //   let value = event.target.value.replace(/\D/g, '') // Remove non-digit characters

  //   // ESI Number formatting (17 digits): XX-XX-XXXXXX-XXX-XXXX
  //   if (value.length > 2 && value.length <= 7) {
  //     value = value.slice(0, 2) + '-' + value.slice(2)
  //   } else if (value.length > 7 && value.length <= 10) {
  //     value = value.slice(0, 2) + '-' + value.slice(2, 7) + '-' + value.slice(7)
  //   } else if (value.length > 10 && value.length <= 13) {
  //     value =
  //       value.slice(0, 2) +
  //       '-' +
  //       value.slice(2, 7) +
  //       '-' +
  //       value.slice(7, 10) +
  //       '-' +
  //       value.slice(10)
  //   } else if (value.length > 13) {
  //     value =
  //       value.slice(0, 2) +
  //       '-' +
  //       value.slice(2, 7) +
  //       '-' +
  //       value.slice(7, 10) +
  //       '-' +
  //       value.slice(10, 13) +
  //       '-' +
  //       value.slice(13, 17)
  //   }

  //   // Update the input value with formatted ESI No.
  //   event.target.value = value
  // }

  get totalEarnings(): number {
    const standard = this.salaryDataArr
      .filter((x: any) => x[0] !== 'otherComponents')
      .reduce((sum: number, x: any) => sum + (x[1] || 0), 0)

    const others =
      this.employeeSalaryDetails?.salaryData?.otherComponents
        ?.filter((x: any) => x.componentType !== 'customDeductions')
        ?.reduce((sum: number, x: any) => sum + (x.employerContribution || 0), 0) || 0
    // return standard + others
    return standard
  }
  // get totalEarnings(): number {
  //   const standard = this.salaryDataArr
  //     .filter((x: any) => x[0] !== 'otherComponents')
  //     .reduce((sum: number, x: any) => sum + (x[1] || 0), 0)

  //   const others =
  //     this.employeeSalaryDetails?.salaryData?.otherComponents?.reduce((sum: number, x: any) => {
  //       // ✅ include ESI Old employer contribution even if it's in customDeductions
  //       const isESIOld = x.name === 'ESI Old'
  //       if (isESIOld) {
  //         return sum + (x.employerContribution || 0)
  //       }

  //       // ❌ skip all other custom deductions
  //       if (x.componentType === 'customDeductions') {
  //         return sum
  //       }

  //       // ✅ normal employer contributions
  //       return sum + (x.employerContribution || 0)
  //     }, 0) || 0

  //   return standard + others
  // }

  get totalDeductions(): number {
    return (
      this.employeeSalaryDetails?.salaryData?.otherComponents
        ?.filter((x: any) => x.componentType === 'customDeductions')
        ?.reduce(
          (sum: number, x: any) =>
            sum + (x.employeeContribution || 0) + (x.employerContribution || 0),
          0
        ) || 0
    )
  }

  onLogsTabClick(event: Event): void {
    const element = event.currentTarget as HTMLElement

    const isOpening = element.classList.contains('collapsed')

    if (!isOpening) {
      this.loadTasklogs()
    }
  }
  onPermissionSetTabClick(event: Event): void {
    const element = event.currentTarget as HTMLElement

    const isOpening = element.classList.contains('collapsed')

    if (!isOpening) {
      this.store.dispatch(
        loadAssignedPermissionSets({
          id: this.employeeId,
          page: this.currentPage,
          limit: this.pageLimit
        })
      )
    }
  }
  // onLeavePolicyTabClick(event: Event): void {
  //   const element = event.currentTarget as HTMLElement

  //   const isOpening = element.classList.contains('collapsed')

  //   if (!isOpening) {
  //     this.leavePolicyService.assignedPolicyByUserId(this.employeeId).subscribe({
  //       next: policies => {
  //         this.leavePolicies = policies
  //       },
  //       error: err => {
  //         console.error('Error fetching policies:', err)
  //       }
  //     })
  //   }
  // }

  loadLeavePolicies() {
    this.leavePolicyService.assignedPolicyByUserId(this.employeeId).subscribe({
      next: (policies: any[]) => {
        this.leavePolicies = policies
      },
      error: (err: any) => {
        console.error('Error fetching policies:', err)
      }
    })
  }

  onLeavePolicyTabClick(event: Event) {
    this.activeTab = 'leave'
    this.loadLeavePolicies()
  }

  onSalaryTabClick(event: Event): void {
    const element = event.currentTarget as HTMLElement

    const isOpening = element.classList.contains('collapsed')

    if (!isOpening) {
      this.store
        .select(selectEmployeeSalary)
        .pipe(
          filter(data => !!data),
          takeUntil(this.destroyed$)
        )
        .subscribe(data => {
          if (data === null) return
          this.employeeSalaryDetails = data
          this.salaryCompliancesList = this.employeeSalaryDetails?.statutoryCompliances

          // 20 may 2026
          this.salaryDataArr = Object.entries(this.employeeSalaryDetails?.salaryData || {})
            .filter(([key, value]) => !(key === 'pf' && value === 0))

            .flatMap(([key, value]: any) => {
              // ✅ Handle otherComponents separately
              if (key === 'otherComponents' && Array.isArray(value)) {
                return (
                  value
                    // before 8 june 2026
                    // ✅ Only custom deductions + NOT included in CTC
                    // .filter((item: any) => {
                    //   const type = (
                    //     item?.componentType ||
                    //     item?.info?.componentType ||
                    //     item?.info?.componenttype ||
                    //     ''
                    //   )
                    //     .toString()
                    //     .replace(/\s|_/g, '')
                    //     .toLowerCase()

                    //   return (
                    //     [
                    //       'customdeduction',
                    //       'customdeductions',
                    //       'customDeductions',
                    //       'customEarnings',
                    //     ].includes(type) && item?.info?.isInCTC === false
                    //   )
                    // })

                    //8 june 2026
                    .filter((item: any) => {
                      const type = (
                        item?.componentType ||
                        item?.info?.componentType ||
                        item?.info?.componenttype ||
                        ''
                      )
                        .toString()
                        .replace(/\s|_/g, '')
                        .toLowerCase()

                      if (['customearning', 'customearnings', 'customEarnings'].includes(type)) {
                        return true
                      }

                      if (['customdeduction', 'customdeductions'].includes(type)) {
                        return item?.info?.isInCTC === false
                      }

                      return false
                    })

                    .map((item: any) => {
                      // ✅ Match:
                      // ESI
                      // ESI OLD
                      // ESI - OLD
                      // ESI_old
                      // etc.

                      const isESI = /^esi\b/i.test(item?.name || '')

                      const label = isESI
                        ? `${item.name} - Employer Contribution`
                        : item.name || 'Other Component'

                      const val = Number(item.employerContribution || 0) || Number(item.amount || 0)

                      return {
                        label,
                        value: val,
                        isEmployer: isESI
                      }
                    })
                )
              }

              // ✅ Default salary fields
              let label = key

              if (key === 'hra') label = 'HRA'
              else if (key === 'specialAllowance') label = 'Special Allowance'
              else if (key === 'pf') label = "Employee's PF Contribution"
              else if (key === 'basic') label = 'Basic'

              return [
                {
                  label,
                  value,
                  isEmployer: false
                }
              ]
            })

            // ✅ Employer components at bottom
            .sort((a: any, b: any) => {
              return Number(a.isEmployer) - Number(b.isEmployer)
            })

            // ✅ Final format
            .map(({ label, value }: any) => [label, value])
        })

      const isContractor = this.employeeData?.userRoles?.some(
        (r: { role: { name: string } }) => r?.role?.name === 'Contractor'
      )

      if (isContractor) {
        this.store.dispatch(
          payoutAgreementActions.loadPayoutAgreement({
            employeeId: this.employeeId
          })
        )
      } else {
        this.store.dispatch(
          loadSalary({ employeeId: this.employeeId, belongsTo: this.employeeData?.businessId })
        )
        this.store.dispatch(
          loadAllSalaries({ employeeId: this.employeeId, belongsTo: this.employeeData?.businessId })
        )
      }
    }
  }

  onEducationTabClick(event: Event): void {
    const element = event.currentTarget as HTMLElement

    const isOpening = element.classList.contains('collapsed')

    if (!isOpening) {
      const params = this.employeeId ? `userId=${encodeURIComponent(this.employeeId)}` : null
      this.store.dispatch(loadEducations({ params }))
    }
  }

  // onBankTabClick(): void {
  //   const params = this.employeeId ? `userId=${encodeURIComponent(this.employeeId)}` : null
  //   this.store.dispatch(loadBanks({ params }))
  // }
  onBankTabClick(event: Event): void {
    const element = event.currentTarget as HTMLElement

    const isOpening = element.classList.contains('collapsed')

    if (!isOpening) {
      const params = this.employeeId ? `userId=${encodeURIComponent(this.employeeId)}` : null
      this.store.dispatch(loadBanks({ params }))
    }
  }

  onOffBoardTabClick(event: Event): void {
    const element = event.currentTarget as HTMLElement

    const isOpening = element.classList.contains('collapsed')

    if (!isOpening) {
      this.loadOffboarding()
    }
  }

  openViewPermissionSetModal(content: TemplateRef<any>, permissionSet: any) {
    this.resetPermissions()

    if (permissionSet?.permissions) {
      this.setPermissionsState(permissionSet.permissions)
    }

    this.modalTitle = 'View Permission Set: ' + (permissionSet?.name || '')

    this.modalRef = this.modalService.open(content, {
      scrollable: true,
      centered: true,
      size: 'lg'
    })
  }

  toggleSalaryDetails(salary: EmployeeSalary): void {
    this.selectedSalaryId = this.selectedSalaryId === salary.id ? null : salary.id
  }

  trackSalaryById(index: number, salary: { id: string }): string {
    return salary.id
  }

  /**
   * Supports both:
   * 2026-10
   * October 2026
   * 2026-10-01
   */
  getPayoutDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null
    }

    const normalizedValue = /^\d{4}-\d{2}$/.test(value) ? `${value}-01` : value

    const date = new Date(normalizedValue)

    return Number.isNaN(date.getTime()) ? null : date
  }

  getComponentInfo(
    info: import('../../../../store/salary-components/salary-components.state').Info
  ): { key: string; value: unknown }[] {
    return Object.entries(info).map(([key, value]) => ({ key, value }))
  }
}
